import type { StudyGuideTutorBinding } from "../utils/study-guide-tutor-lifecycle";
import { Timestamp } from "firebase/firestore";
import { LiveRecording } from "../zustand/liveRecordingStore";
import { VoiceQuotaInformation } from "../voice/core/voiceQuotaHelpers";
import type { AcquisitionAttribution } from "../utils/acquisition-attribution";

// ****** WEB ONLY (not on Extension) ****** //
type HeaderType =
  | "noHeader"
  | "jungleLogoAndProfileMenu"
  | "jungleLogo"
  | "backArrowAndJungleLogo"
  | "backArrowAndTitle"
  | "homePage"
  | "generatedCardsHeader"
  | "backArrowAndProfileMenu"
  | "headerForReviewExperience"; //shows a header with only an "X" button that the user can press to leave the review
export default HeaderType;

export enum GetPageFromUrlErrorTypes {
  DocumentIsPrivateGoogleDocs = "Document is a private Google Docs",
  WebpageHasSecurityProblems = "Webpage has security access problems",
  NotSupportedFiles = "The file type you uploaded is not currently supported",
  GoogleDriveSizeLimitExceeded = "The file is too large. Download it as PDF first, then upload the PDF.",
  TimeOutOrLimitExceeded = "An error occurred: Failed to convert Google Doc to PDF. Please check the document or try again later.",
}

export const PAGE_HEADER_HEIGHT_MOBILE = "80px";
export const PAGE_HEADER_HEIGHT = "64px";
export const WIDTH_OF_CARD_COMPONENT_WHEN_SHOWING_TREE_IMAGE = "552px";
export const MAX_WIDTH_FOR_ANSWER_AND_FEEDBACK_CONTENT = 552;
export const MAX_WIDTH_FOR_MCAT_PASSAGE_REVIEW_CONTENT = 660;
export const MONTHLY_RETENTION_DISCOUNT_PERCENTAGE_OFF = 40;
export const ANNUAL_RETENTION_DISCOUNT_PERCENTAGE_OFF = 30;
export const RETENTION_DISCOUNT_DURATION_FOR_MONTHLY_SUBSCRIPTIONS = 3;

// ****** ENUMS ****** //
// ****** ENUMS ****** //
export enum GlobalStateActionTypes {
  setShouldShowFreeResponseAnswerFeedbackView, // web only

  setIsRegeneratingQuestions,
  setIsUploadingPDF,
  setIsAnswerTextareaFocused,
  setDidUserSelectIDontKnow,
  setAnswerTextareaValue,
  setPreviousQuestionAndAnswerObjectToReview,
  setCurrentUser,
  updateCurrentUser,
  addAnkiDecks,
  setLastDeckThatWasSavedTo,
  setDidChooseToSaveAllGeneratedFlashcards,
  setAreFlashcardsBeingGenerated,
  setHasAnkiBeenSetUp,
  setDeckToEdit,
  setIsFlashcardBeingEdited,
  setShowIsLocalPdfError,
  setYoutubeTranscriptArrayText,
  setIsEnabledGenerateFlashcardButton,
  setPdfNumberOfPages,
  setPageTextSentencesArray,
}

// SPACED REPETITON
export const enum SpacedRepetitionReviewStatus {
  NotStarted = "not_started", // review has not been started
  InProgress = "in_progress", // review was started but was not finished — e.g., user is on card 21 of 40
  Completed = "completed", // A review is considered complete only after they reach the finish screen of the review. The user does not have to answer all questions for a review to be considered completed
}

export const enum ReviewTypes {
  DailyReview = "DailyReview",
  ExamReview = "ExamReview",
  DeckReview = "DeckReview",
}

export const enum LocationFromWhereReviewWasStarted {
  DeckOverview = "DeckOverview",
  HomePage = "HomePage",
  GenerationHistory = "GenerationHistory",
}

export const enum CardItemLocationName {
  DeckOverview = "DeckOverview",
}

export type SpacedRepetitionReviewData = {
  id: string;
  card_ids_for_review: Array<any>;
  date_created: Date;
  index_of_last_card_reviewed: number;
  review_status: string;
  user_id: string;
  is_first_spaced_repetition_review?: boolean;
  card_topics_sorted_by_frequency?: Array<string>;
};

export const enum CardType {
  // cards with multiple choices
  RegularMultipleChoiceQuestion = "Multiple Choice Question",
  CaseScenarioMultipleChoiceQuestion = "Case Scenario Multiple Choice Question",
  TrueOrFalseQuestion = "True/False Question",
  USMLEStep1MCQ = "USMLE Step 1 MCQ",

  // free response questions
  RegularFreeResponseQuestion = "Understanding Question", // these are the questions used for the "learn the key concepts" step
  CaseScenarioFreeResponseQuestion = "Case Scenario Question",

  // image occlusion
  ImageOcclusion = "Image Occlusion",

  // deprecated
  OpenEndedQuestion = "Open Ended Question", // DEPRECATED with release of learning jourenys (2.22.24)
  PreLearningJourneyQuestion = "Pre Learning Journey Question", // If CardType = OpenEndedQuestion, then we will change type to PreLearningJourneyQuestion to avoid confusion.
}

export enum TrueOrFalse {
  True = "True",
  False = "False",
}

export const enum QuestionDifficultyGroup {
  Basic = "Basic",
  Advanced = "Advanced",
}

export const enum QuestionGenerationTemplateId {
  Bloom = "bloom",
  USMLEStep1 = "usmle_step1",
  MCAT = "mcat",
  TableMCQ = "table_mcq",
}

export const enum QuestionTaxonomy {
  Remember = "Remember",
  Understand = "Understand",
  Apply = "Apply",
  Analyze = "Analyze",
  Evaluate = "Evaluate",
}

export type CardTypeWithDifficulty = {
  cardType: CardType;
  difficultyGroup?: QuestionDifficultyGroup;
};

export const enum KnowledgeUnderstandingRating {
  notSure = "not sure",
  okay = "okay",
  iKnowIt = "i know it",
}

export const enum LevelForAmountOfCardsToGenerate {
  veryLow = "very low",
  low = "low",
  medium = "medium",
  high = "high",
}

export const enum UserType {
  Student = "Student",
  TeacherOrProfessor = "Teacher / Professor",
  Other = "Other",
}

export const enum SignUpAndOnboardingModalView {
  LoginView = "LoginView",
  SignInOrSignUpOptionsView = "SignInOrSignUpOptionsView",
  SignUpWithEmailView = "SignUpWithEmailView",
  OnboardingStudentTypeView = "OnboardingStudentTypeView",
  OnboardingAddNameView = "OnboardingAddNameView",
  OnboardingUniversitySelection = "OnboardingUniversitySelection",
}

export const enum SignUpAndOnboardingModalActivatedReason {
  ViewedThreeCards = "ViewedThreeCards",
  SaveCards = "SaveCards",
  GeneratingSecondSetOfCards = "GeneratingSecondSetOfCards",
  UserWantsToTryAiImageOcclusion = "UserWantsToTryAiImageOcclusion",
  UserWantsToStartLearningJourney = "UserWantsToStartLearningJourney",
  UserWantsToStartStudyingCards = "UserWantsToStartStudyingCards",
  SaveCardsForLater = "SaveCardsForLater",
  SaveSharedDeckCardsForLater = "SaveSharedDeckCardsForLater",
  JoinClass = "JoinClass",
  ContinuePastMiniLesson = "ContinuePastMiniLesson",
  OnlyShowSignUpModal = "OnlyShowSignUpModal",
  AfterQuestionPruningScreen = "AfterQuestionPruningScreen",
  // A signed-out visitor reached their first set's first review without signing up (the browser's back button after
  // the questions screen): its question 1 waits under a sign-up sheet (first-user-experience/answer-first/). The name
  // is from T1's ended "answer first" side, whose visitors answered three questions first.
  AnsweredFirstQuestionsBeforeSigningUp = "AnsweredFirstQuestionsBeforeSigningUp",
  // A visitor reading a shared study guide (features/study-guides/signedOutGate.ts): chatting or calling Monkey,
  // practising its questions (a dialog that cannot be closed, over question 1), or making their own guide.
  StudyGuideKeepStudying = "StudyGuideKeepStudying",
  StudyGuideStudyQuestions = "StudyGuideStudyQuestions",
  StudyGuideCreateOwn = "StudyGuideCreateOwn",
}

export const enum StudentType {
  HighSchool = "High School",
  Undergraduate = "Undergraduate",
  Masters = "Master's",
  PhD = "PhD",
  Medical = "Medical",
  PreMed = "Pre-Med",
  Dental = "Dental",
  Nursing = "Nursing",
  Veterinary = "Veterinary",
  Pharmacy = "Pharmacy",
  Teacher = "Teacher",
  Law = "Law",
  Other = "Other",
  NotStudent = "Not a student",
  OtherHealthRelated = "Other Health Related",
  OtherGraduate = "Other Graduate",
}

// The student types we consider "health students" for health-specific product
// naming and branding.
export const HEALTH_RELATED_STUDENT_TYPES: readonly StudentType[] = [
  StudentType.Medical,
  StudentType.Dental,
  StudentType.Pharmacy,
  StudentType.Veterinary,
  StudentType.PreMed,
  StudentType.Nursing,
  StudentType.OtherHealthRelated,
];

// Whether the given student type is a health-profession track. Returns false
// for a null / undefined student type (e.g. a user who hasn't picked one yet).
export function isHealthRelatedStudentType(studentType: StudentType | null | undefined): boolean {
  return !!studentType && HEALTH_RELATED_STUDENT_TYPES.includes(studentType);
}

export enum ContentMediumType {
  Webpage = "Webpage",
  PDF = "PDF",
  YouTubeVideo = "YouTubeVideo",
  Video = "Video",
  Audio = "Audio",
  Image = "Image",
  AnkiTxt = "AnkiTxt",
  SVG = "SVG",
}

export enum FeedbackType {
  Annoying = "Annoying",
  Love = "Love",
  FeatureRequest = "Feature Request",
  Bug = "Bug",
  GeneralFeedback = "General Feedback",
}

// ****** INTERFACES ****** //
// ****** INTERFACES ****** //
interface DeckColor {
  background: string;
  boxShadow: string;
  borderColor: string;
}

// ****** CONST ****** //
// ****** CONST ****** //

// collection names
export const USERS_COLLECTION_NAME = "users";
export const CARDS_SAVED_COLLECTION_NAME = "cards_saved";
export const CARDS_DELETED_COLLECTION_NAME = "deleted_cards";
export const ARTICLES_SAVED_COLLECTION_NAME = "articles_saved";
export const DECK_COLLECTION_NAME = "decks";
// "Prove mastery for 20% off" — lifetime attempt cap. Mirrors the server's
// MAX_MASTERY_ATTEMPTS in jungle-backend routes/voice_mastery_routes.py (the
// backend is authoritative; this only drives offer-visibility/copy).
export const MASTERY_CHALLENGE_MAX_ATTEMPTS = 3;

export const DELETED_DECK_COLLECTION_NAME = "deleted_decks";
export const EXAMS_COLLECTION_NAME = "exams";
export const KEY_CONCEPTS_FROM_DOCUMENT_COLLECTION_NAME = "key_concepts_from_document";
export const PDF_IMAGES_TEMP_COLLECTION_NAME = "pdf_images_temp";
export const IMAGES_FOR_PDF_IMAGES_TEMP_COLLECTION_NAME = "images";
export const USER_INTERACTIONS_FOR_DEVICE = "user_interactions_for_device_id";
export const USER_INTERACTIONS_FOR_USER = "user_interactions_for_user_id";
export const ANSWERS_FROM_USER_AND_AI_FEEDBACK_COLLECTION_NAME = "answers_from_user_and_ai_feedback";
export const SESSION_LISTENER_COLLECTION_NAME = "session_listener";
export const FLASHCARDS_COLLECTION_NAME = "flashcards";
export const TOPIC_UNDERSTANDING_PAGES_DATA_SUBCOLLECTION_NAME = "topic_understanding_pages_data";
export const FREE_RECALL_ATTEMPTS_SUBCOLLECTION_NAME = "free_recall_attempts";
export const SPACED_REPETITION_REVIEWS_COLLECTION_NAME = "spaced_repetition_reviews";
export const FREE_RECALL_ATTEMPTS_BY_USER_COLLECTION_NAME = "free_recall_attempts_by_user_id";
export const APP_SETTINGS_COLLECTION_NAME = "app_settings";
export const KEY_CONCEPTS_TO_DECKS_MAPPING_NAME = "key_concepts_to_decks_mapping";
export const FILTERED_CONCEPTS_NAME = "filtered_concepts";
export const LEADERBOARD_COLLECTION_NAME = "leaderboard";
export const LEADERBOARD_LEAGUES_COLLECTION_NAME = "leaderboard_leagues";
export const LEADERBOARD_LEAGUE_GROUPS_COLLECTION_NAME = "leaderboard_league_groups";
export const STREAKS_COLLECTION_NAME = "streaks";
export const TEST_STREAKS_COLLECTION_NAME = "test_streaks";
export const COUNTRY_AND_SCHOOLS_COLLECTION_NAME = "countries_and_schools";
export const VIDEO_ID_TO_YOUTUBE_VIDEO_TRANSCRIPT_COLLECTION_NAME = "video_id_to_youtube_video_transcript";
export const GET_DATA_FROM_FILE_PROCESSING_STATUS_COLLECTION_NAME = "get_data_from_file_processing_status";
export const TEXT_PAGES_FOR_PROCESSING_STATUS_COLLECTION_NAME = "text_pages";
export const GRATITUDE_ENTRIES_BY_USER_COLLECTION_NAME = "gratitude_entries_by_user";
export const USER_TIME_CAPSULE_MESSAGES_COLLECTION_NAME = "user_time_capsule_messages";

// Support knowledge base + end-user support chatbot conversations.
// Note: `support_user_conversations` is the END-USER chatbot store; it is
// intentionally separate from the admin AI assistant collection
// `user_support_conversations` to keep the two domains decoupled.
export const SUPPORT_KNOWLEDGE_BASE_ITEMS_COLLECTION_NAME = "support_knowledge_base_items";
export const SUPPORT_USER_CONVERSATIONS_COLLECTION_NAME = "support_user_conversations";
export const SUPPORT_USER_CONVERSATION_MESSAGES_SUBCOLLECTION_NAME = "messages";
// Per-turn agent trace written by the worker (intent classification, tool
// calls, model completions, investigation + email steps). Rendered as the
// timeline in the admin thread sidebar so an admin can see exactly what the
// agent did. See `docs/support-ai/05-implementation-plan.md` §8.
export const SUPPORT_USER_CONVERSATION_TRACE_EVENTS_SUBCOLLECTION_NAME = "trace_events";

export type SupportUserConversationStatus = "open" | "waiting_for_customer" | "pending_to_check" | "done" | "ignored";

export type SupportUserConversationMessageRole = "user" | "bot" | "admin";

export type SupportUserConversationFeedbackTypeTag =
  "Annoying" | "Love" | "Feature Request" | "Bug" | "General Feedback";

// Catalog of admin-curated labels (Plain.com-style) used to auto-classify
// support conversations and drive label-specific bot behavior via `ai_action`.
// Stored separately from individual conversations so the catalog can be
// edited / soft-deleted without losing label snapshots on historical convos.
export const SUPPORT_THREADS_LABELS_COLLECTION_NAME = "support_threads_labels";

export type SupportThreadsLabelStatus = "active" | "deleted";

// String identifier for the channel a `support_user_conversations.channel`
// document lives in. Kept lowercase to match the value the worker normalizes
// inbound payloads to (`normalizeChannelOrDefault`).
export type SupportChannelKind = "chat" | "gmail";

// Collection of admin-curated Gmail inboxes the support feature mirrors.
// Each document represents ONE Workspace shared mailbox (e.g. `support@`,
// `billing@`, `partners@`). Admins can connect as many inboxes as they need;
// every inbox owns its own watch lifecycle, Pub/Sub topic, behavior toggles,
// and Service Account secret pointer.
export const SUPPORT_GMAIL_INBOXES_COLLECTION_NAME = "support_gmail_inboxes";

// Lifecycle status of a Gmail inbox. Soft-deleted rows survive so that
// conversations that referenced them keep rendering correctly.
export type SupportGmailInboxStatus = "active" | "deleted";

// limitations for different plans
export const NUM_OF_ADDITIONAL_GENERATION_CARDS_BY_REFERRAL = 2;
export const MAX_NUMBER_OF_REFERRED_USERS_WITH_INCENTIVE = 3;

export const NUM_OF_FREE_MONTHLY_GENERATIONS_ALLOWED = 2;
export const NUM_OF_FREE_MONTHLY_GENERATIONS_ALLOWED_FOR_CONSTRAINED_GEOGRAPHIES = 1;
export const NUM_OF_FREE_AI_IMAGE_OCCLUSIONS_ALLOWED = 1;
export const NUM_OF_FREE_MONTHLY_LIVE_RECORDINGS_ALLOWED = 5;
export const NUM_OF_FREE_ADVANCED_QUESTION_GENERATIONS_ALLOWED = 4;
// Templated (e.g. USMLE Step 1) question generations per month for every plan
// below Elite — Elite is unlimited. Mirrors the advanced-questions mechanism.
export const NUM_OF_FREE_TEMPLATED_QUESTION_GENERATIONS_ALLOWED = 1;
export const NUM_OF_ANSWER_FEEDBACKS_ALLOWED_PER_TIME_LIMIT = 5; // currently not being used
export const NUM_OF_CHAT_MESSAGES_ALLOWED_PER_TIME_LIMIT = 8;

export const MAX_PDF_PAGES_ALLOWED_FOR_FREE = 30;
export const MAX_VIDEO_MIN_ALLOWED_FOR_FREE = 30;
export const NUM_OF_HOURS_UNTIL_CHAT_AND_ANSWER_FEEDBACKS_RESET = 1;

export const MAX_VIDEO_MIN_ALLOWED_FOR_SUPER_LEARNER_PLAN = 240;
export const MAX_PDF_PAGES_ALLOWED_FOR_SUPER_LEARNER_PLAN = 1000;

export const WIDTH_OF_FULL_HEIGHT_CHAT_PANEL_ON_DESKTOP = "470px";

// streak limits
export const NUM_OF_STREAK_FREEZES_PER_MONTH = 2;
export const NUM_OF_QUESTIONS_NEEDED_TO_EXTEND_THE_STREAK = 10;

// mini review limitations. The first review's length and mix are FIRST_REVIEW_QUESTION_COUNT and its mix
// (src/first-experience-tests/firstExperienceGelRules.ts; 5 since the first-experience test T2 ended), which replaced the 5 + 2 / 7 split of a 2025
// experiment that ended when every generation moved to the new architecture.
export const MAX_NUM_OF_MULTIPLE_CHOICE_QUESTIONS_FOR_MINI_LESSON_FOR_BASIC_TAXONOMY = 3;
export const MAX_NUM_OF_MULTIPLE_CHOICE_QUESTIONS_FOR_MINI_LESSON_FOR_APPLIED_TAXONOMY = 2;
export const MAX_NUM_OF_UNDERSTANDING_QUESTIONS_FOR_MINI_LESSON = 1;
export const MAX_NUM_OF_CASE_SCENARIO_QUESTIONS_FOR_MINI_LESSON = 1;

// question pruning limits
export const MIN_NUM_OF_QUESTIONS_FOR_QUESTION_PRUNING = 2;
export const MAX_NUM_OF_QUESTIONS_FOR_QUESTION_PRUNING = 15;

// view all cards
export const CARDS_BATCH_SIZE_TO_SHOW_IN_DECK_OVERVIEW_PAGE = 10;

export const wisdoliaJungleUrl = "https://wisdolia-images.s3.eu-north-1.amazonaws.com/jungle.jpg";
export const JUNGEL_DESKTOP_APP_DOWNLOAD_URL =
  "https://jungle-desktop-app.s3.us-west-2.amazonaws.com/Jungle-1.0.0-arm64.dmg";
export const MIN_WIDTH_OF_TABLET_PORTAIT_VIEW = 767;
export const MAX_WIDTH_OF_TABLET_PORTAIT_VIEW = 1024;
export const HEIGHT_OF_CARD_NAVIGATOR = "108px";
/** Matches card navigator chrome so the chat composer top border aligns with the review footer line. */
export const CHAT_COMPOSER_FOOTER_MIN_HEIGHT = HEIGHT_OF_CARD_NAVIGATOR;
export const HEIGHT_OF_CARD_NAVIGATOR_FOR_ANSWER = "132px";
// US list prices (USD) — used only as the purchase-tracking fallback when
// subscription_price_of_last_payment_in_usd is missing (PaymentSuccessConfirmationPage).
// Monthly is 2.5x the annual per-month rate since 2026-09-25; existing subscribers keep their old price.
export const PRICE_FOR_ELITE_ANNUAL_PLAN = 180;
export const PRICE_FOR_ELITE_MONTHLY_PLAN = 37;
export const PRICE_FOR_SUPER_LEARNER_ANNUAL_PLAN = 72;
export const PRICE_FOR_SUPER_LEARNER_MONTHLY_PLAN = 15;
export const PRICE_FOR_MEGA_MIND_ANNUAL_PLAN = 60;
export const PRICE_FOR_MEGA_MIND_MONTHLY_PLAN = 8;
export const NUM_OF_TREES_PER_JUNGLE_PAGE = 27;
export const UNDERSTANDING_RATING_FOR_GETTING_FREE_RESPONSE_AND_CASE_SCENARIO_CORRECT = 6;
export const UNDERSTANDING_RATING_FOR_GETTING_MCQ_CORRECT = 3;
export const UNDERSTANDING_RATING_FOR_GETTING_TRUE_OR_FALSE_QUESTION_CORRECT = 2;
export const UNIVERSITY_NAME_FILTER_FOR_LEADERBOARD = "university";
export const GLOBAL_FILTER_FOR_LEADERBOARD = "global";
export const MIN_NUM_OF_PEOPLE_TO_SHOW_UNIVERSITY_FILTER = 5;
export const HEIGHT_OF_CARD_NAVIGATOR_FOR_ANSWER_MOBILE = "182px";
export const WISDOLIA_LANDING_PAGE_DOMAIN = "https://jungleai.com";
export const WISDOLIA_APP_DOMAIN = "https://app.jungleai.com";
export const FIREBASE_VAP_ID_KEY =
  "BONEECWW-0AvxCTmScv2O-czzalCtP5zbzPksdEqFGIM8kVImwoETF7I-aVsGUJpOeYyWuskKRTzuXNODp57xdI";

// Voice-tutor discovery nudge: on the user's Nth press of the post-answer
// explain button, auto-launch a voice call instead of the text explanation.
// Fires once per user; excludes users who have already started a voice call.
export const EXPLAIN_PRESSES_TO_AUTO_LAUNCH_VOICE = 3;
export const PLAIN_EMAIL_HMAC_SECRET = "dbf1875071023fafcfc85c0b869d8de05b34cc0b257d63fa045f1ad215962da0";
export const PLAIN_LIVE_CHAT_APP_ID = "liveChatApp_01KMN22DK8C2ZNAPJF0QJ6D950";
// export const JUNGLE_APP_LOCALHOST_DOMAIN = "http://localhost:3000";
// export const JUNGLE_BACKEND_PROD_DOMAIN = "https://wisdolia-backend.herokuapp.com";
// export const JUNGLE_BACKEND_PROD_DOMAIN = "https://d2zy2qw1c0utjq.cloudfront.net";
// export const JUNGLE_BACKEND_PROD_DOMAIN = "https://wisdolia-backend-9v0s.zeet-wisdolia.zeet.app";
// export const JUNGLE_BACKEND_PROD_DOMAIN = "https://wisdolia-backend-2-ywnn.zeet-wisdolia.zeet.app";
// export const JUNGLE_BACKEND_PROD_DOMAIN = "https://d2x8bd7jlht0nk.cloudfront.net";
// export const JUNGLE_BACKEND_PROD_DOMAIN = "https://wisdolia-test.herokuapp.com";
// export const JUNGLE_BACKEND_PROD_DOMAIN = "https://wisdolia-backend-2-ywnn-gunicorn-testing.zeet-wisdolia.zeet.app";
// export const JUNGLE_BACKEND_PROD_DOMAIN = "https://wisdolia-backend-2-ywnn.zeet-wisdolia.zeet.app";
// export const JUNGLE_BACKEND_PROD_DOMAIN = "https://nov-lawsuit-fighter-performing.trycloudflare.com";
// export const JUNGLE_BACKEND_PROD_DOMAIN =  process.env.REACT_APP_BACKEND_URL || "https://wisdolia-backend-2-ywnn.zeet-wisdolia.zeet.app";
// export const JUNGLE_BACKEND_PROD_DOMAIN = "https://cbackend.jungleai.com";
// This is the PRODUCTION worker, and it stays production regardless of where
// the page is served from. It briefly resolved to http://localhost:8787 when
// window.location.host contained "localhost", which contradicted the invariant
// documented on getBackendDomain below and silently overrode
// VITE_BACKEND_MODE=prod: anyone opening the app locally to reproduce a
// production issue was quietly pointed at their own machine instead, and the
// unknown-mode path stopped failing safe to production. Local development
// selects a local backend through VITE_BACKEND_MODE, which is what
// `npm run start:local` and `npm run start:local-cf` already do.
const CLOUDFLARE_WORKER_PROD_HTTP_URL = "https://apiv1.jungleai.com";

// Backend mode: "prod" (default), "local", or "local-cf"
// Set via VITE_BACKEND_MODE env var (see npm scripts in package.json)
const backendMode = (import.meta.env.VITE_BACKEND_MODE as string) || "prod";
const normalizeLocalServiceUrl = (configuredUrl: string | undefined, fallbackUrl: string): string =>
  (configuredUrl?.trim() || fallbackUrl).replace(/\/+$/, "");

const LOCAL_PYTHON_BACKEND_URL = normalizeLocalServiceUrl(
  import.meta.env.VITE_LOCAL_PYTHON_BACKEND_URL as string | undefined,
  "http://localhost:5003"
);
const LOCAL_CLOUDFLARE_WORKER_URL = normalizeLocalServiceUrl(
  import.meta.env.VITE_LOCAL_CLOUDFLARE_WORKER_URL as string | undefined,
  "http://localhost:8787"
);

const getBackendDomain = (): string => {
  // backendMode (set via VITE_BACKEND_MODE) is the single source of truth for
  // which backend to use. It is intentionally NOT influenced by whether the
  // frontend happens to be served from localhost: running `npm start` on
  // localhost still talks to the production backend unless VITE_BACKEND_MODE
  // explicitly selects a local mode (npm run start:local / start:local-cf).
  if (backendMode === "local") {
    // Local Python backend directly (no Cloudflare worker)
    return LOCAL_PYTHON_BACKEND_URL;
  }
  if (backendMode === "local-cf") {
    // Local Cloudflare worker, which proxies `/core/*` on to the Python
    // backend. The `/core` suffix is REQUIRED here -- without it, callers
    // like `${JUNGLE_BACKEND_PROD_DOMAIN}/agentic-chat` land on
    // `http://localhost:8787/agentic-chat`, which the worker has no route
    // for and returns 404. Mirrors the canary path below, which already
    // includes `/core`. Worker-native routes (voice token mint, etc.) hit
    // `JUNGLE_CLOUDFLARE_BASE_URL` instead, which deliberately keeps the
    // bare worker URL because those routes are NOT under `/core/`.
    return `${LOCAL_CLOUDFLARE_WORKER_URL}/core`;
  }
  return `${CLOUDFLARE_WORKER_PROD_HTTP_URL}/core`;
};

export const JUNGLE_BACKEND_PROD_DOMAIN = getBackendDomain();

export const JUNGLE_CLOUDFLARE_BASE_URL =
  backendMode === "local" || backendMode === "local-cf" ? LOCAL_CLOUDFLARE_WORKER_URL : CLOUDFLARE_WORKER_PROD_HTTP_URL;

// Support development often needs a hybrid stack: normal product data comes
// from production while `/support/*` is isolated in a local Worker + Firestore
// emulator. Keeping this override support-specific prevents that emulator from
// making the signed-in tester's real decks appear empty.
const configuredSupportApiBaseUrl = (import.meta.env.VITE_SUPPORT_API_BASE_URL as string | undefined)?.trim();
export const JUNGLE_SUPPORT_API_USES_SEPARATE_BACKEND = Boolean(configuredSupportApiBaseUrl);
export const JUNGLE_SUPPORT_API_BASE_URL =
  configuredSupportApiBaseUrl === "same-origin" && typeof window !== "undefined"
    ? window.location.origin
    : configuredSupportApiBaseUrl || JUNGLE_CLOUDFLARE_BASE_URL;
// Fail closed until the support routes are deployed. The isolated local stack
// supplies its own API override and is enabled automatically; a same-backend
// production release must opt in only after those routes answer successfully.
export const JUNGLE_SUPPORT_UI_IS_ENABLED =
  JUNGLE_SUPPORT_API_USES_SEPARATE_BACKEND || import.meta.env.VITE_SUPPORT_UI_ENABLED === "true";

// Tutor clients only (Tutor, source uploads, protected study). The local production-account stack
// (jungle-backend cloudflare-worker/scripts/tutor-local) sets "same-origin" so Tutor calls reach the
// local Worker through the dev server while every other call goes straight to production. Only that
// one value is accepted, and only in a development build served from localhost; arbitrary URLs throw.
export function tutorApiBaseUrl(
  configured: string | undefined,
  isDevelopment: boolean,
  location: Pick<Location, "hostname" | "origin"> | undefined
): string {
  if (!configured) return JUNGLE_CLOUDFLARE_BASE_URL;
  if (configured === "same-origin" && isDevelopment && location && ["localhost", "127.0.0.1"].includes(location.hostname)) {
    return location.origin;
  }
  throw new Error("tutor_api_base_url_invalid");
}
const configuredTutorApiBaseUrl = (import.meta.env.VITE_TUTOR_API_BASE_URL as string | undefined)?.trim();
export const JUNGLE_TUTOR_API_BASE_URL = tutorApiBaseUrl(
  configuredTutorApiBaseUrl,
  import.meta.env.DEV === true,
  typeof window === "undefined" ? undefined : window.location
);

// Domain for the backend routes that were migrated OFF the Python backend and
// are now served NATIVELY by the Cloudflare worker (2026-06 migration:
// payments discounts/extend/reactivate, occlusion card generation, subdeck
// save, notification + email endpoints, chat feedback, streaks, admin voice
// pages, mobile checkout — full route list in
// jungle-backend/cloudflare-worker/README.md). Unlike
// JUNGLE_BACKEND_PROD_DOMAIN there is no `/core` suffix and no canary split:
// these routes don't exist behind `/core/*` (that proxies to Modal) — the
// worker handles them itself.
//   - prod:     https://apiv1.jungleai.com (production worker)
//   - local-cf: http://localhost:8787 (local worker serves them natively)
//   - local:    http://localhost:5003 (the Python backend still serves all of
//               them directly, for deliberate worker-bypass debugging)
export const JUNGLE_BACKEND_DOMAIN_FOR_ROUTES_MIGRATED_TO_CLOUDFLARE_WORKER =
  backendMode === "local"
    ? LOCAL_PYTHON_BACKEND_URL
    : backendMode === "local-cf"
      ? LOCAL_CLOUDFLARE_WORKER_URL
      : CLOUDFLARE_WORKER_PROD_HTTP_URL;

// list of emails for premium
export const EMAILS_FOR_GRANTING_PREMIUM_PLAN = [
  "markovic.sindi@stud.hs-fresenius.de",
  "alef.hendrik@stud.hs-fresenius.de",
  "zdanovic.anastassia@stud.hs-fresenius.de",
  "spreitz.nicole@stud.hs-fresenius.de",
  "ligkas.antonia@stud.hs-fresenius.de",
  "feldkord.stefanie@stud.hs-fresenius.de",
  "geiger.tim@stud.hs-fresenius.de",
  "uwe.stoklossa@amdnet.de",
  "philipp.walther@hs-fresenius.de",
];

// FOR UPLOAD AUDIO AND VIDEO FILES
export const mimeTypesFiles = [
  "video/quicktime",
  "video/mp4",
  "audio/mpeg",
  "audio/wav",
  "video/x-msvideo",
  "audio/x-wav",
  "video/avi",
  "audio/ogg",
  "audio/flac",
  "audio/aac",
  "audio/aiff",
  "audio/amr",
  "audio/x-m4a",
  "audio/opus",
  "application/vnd.rn-realmedia",
  "txt/plain",
];
export const acceptedFileTypesForWeb =
  "image/jpeg,image/png,image/webp,image/svg+xml,image/heic,image/heif,.jpeg,.jpg,.jfif,.png,.webp,.svg,.heic,.heif,application/vnd.rn-realmedia,application/vnd.ms-powerpoint,.rm,audio/opus,.opus,.oga,audio/x-m4a,.m4a,audio/amr,.amr,audio/aiff,.aiff,audio/aac,.aac,audio/flac,.flac,.ogg,application/pdf,application/vnd.openxmlformats-officedocument.presentationml.presentation,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/msword,video/quicktime,video/mp4,audio/mpeg,audio/mp3,audio/wav,audio/x-wav,video/x-msvideo,text/plain,text/markdown,.pdf,.doc,.docx,.ppt,.pptx,.txt,.md,.markdown,.mp3,.wav,.mp4,.mov,.avi";
export const acceptedFileTypesForApp =
  "image/jpeg,image/png,image/svg+xml,image/heic,image/heif,application/pdf,.rm,audio/opus,.opus,.oga,audio/x-m4a,.m4a,audio/amr,.amr,application/vnd.ms-powerpoint,audio/aiff,.aiff,audio/aac,.aac,audio/flac,.flac,.ogg,application/vnd.openxmlformats-officedocument.presentationml.presentation,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/msword,audio/mpeg,audio/mp3,audio/wav,audio/x-wav,text/plain,text/markdown,.jpg,.jpeg,.jfif,.png,.svg,.heic,.heif,.pdf,.doc,.docx,.ppt,.pptx,.txt,.md,.markdown,.mp3,.wav";
export const audioExtensions = ["mp3", "wav", "ogg", "flac", "aac", "aiff", "amr", "m4a", "oga", "opus", "rm"];
export const videoExtensions = ["mp4", "mov", "avi"];
export const imageExtensions = ["jpg", "png", "jpeg", "jfif", "svg", "heic", "heif", "webp"];

// ****** TYPES ****** //
// ****** TYPES ****** //
export type PageSetupProps = {
  title?: string;
  showJungleBackground: boolean;
  headerType: HeaderType;
};

export type ChatSidePanelProps = {
  currentFreeResponseAnswerFeedbackObject?: FreeResponseAnswerFromUserWithFeedback;
  currentCardData?: CardData;
  mostRecentUserAnswerForQuestion?: string;
  isViewingChatForASpecificCard?: boolean;
  chatLocationName: ChatLocations;
  showCloseButton?: boolean;
  maxHeight?: string;
  isReviewingRecentMistakes?: boolean;
  initialUserMessageForChat?: string;
  // When true, the chat panel immediately starts a "call monkey" voice call on
  // open. Set by the snackbar's voice-call shortcut and cleared by
  // ChatViewComponent once the call has started.
  autoStartVoiceCall?: boolean;
  // True when this voice call was auto-launched by the "3rd explain press"
  // discovery nudge. Drives the "return to normal text chat" button in the call
  // panel (shown only for this flow, not for normal voice calls).
  isAutoLaunchedExplainVoiceCall?: boolean;
  // The original "explain my mistake" / "explain why it's right" text that the
  // user clicked. Stashed (instead of auto-sending it as a text message) so the
  // "return to normal text chat" button can deliver the text explanation after
  // ending the auto-launched call.
  pendingExplainMessageAfterVoiceExit?: string;
  shouldShowTabsInChatSidePanel?: boolean;
  activeTabId?: SidePanelTab;
  // The outline markdown the summary outline surface is displaying, supplied
  // by that surface so Monkey grounds in exactly what the learner sees (the
  // embedded subdeck view keeps its viewed deck in local state, so the chat
  // panel cannot derive this from the global store). Untruncated;
  // ChatViewComponent caps it per modality before sending.
  summaryOutlineContextText?: string;
  summaryOutlineDeck?: DeckObject;
  studyGuideTutor?: StudyGuideTutorBinding;
  // Legacy property - removed
};

/** Where a quoted study-guide passage or visual is in the guide (the reader's own anchor for words). */
export type GuideQuoteLocator =
  | { type: "text"; scope: string; start: number; end: number; prefix?: string; suffix?: string }
  | { type: "visual"; visual: "figure" | "table" | "process" | "illustration"; section?: string; block?: string; figure?: string };

/** A study-guide passage or visual a chat message quoted, as it was quoted, and where it came from. */
export type GuideQuotedReference = { kind: "text" | "visual"; quote: string; locator?: GuideQuoteLocator };

export type ChatMessage = {
  text_message: string;
  is_message_from_user: boolean;
  // True on the user bubble of a feedback-on-answer turn (the "explain why
  // it's right" / "explain my mistake" message). Lets a retry re-route to the
  // dedicated /explain-answer endpoint instead of the general agentic chat.
  is_feedback_on_answer?: boolean;
  pageUrl: string;
  is_generating_answer?: boolean;
  raindrop_message_event_id?: string;
  chat_message_id?: string;
  follow_up_questions?: string[]; // is only in the assistant message object (`is_message_from_user` is false)
  // Files/screenshots sent with a user message (names and a small image preview only).
  attachments?: Array<{ name: string; kind: "image" | "file"; preview_data_url?: string }>;
  // When the message was sent, in epoch seconds (the backend's stored thread messages carry it too).
  date_created?: number;
  // Where the source video was when this user message was asked from the watch composer (seconds).
  // Sent back in chat_history so the tutor can tell a follow-up from a question about a new moment.
  video_position_seconds?: number;
  // The study-guide passages or visuals this user message quoted (study guide "ask monkey" / "explain it
  // to me"), with where each one is in the guide, so a click on the quote brings the reader back to it.
  quoted_references?: GuideQuotedReference[];
  image_search?: {
    status: "pending" | "done" | "skipped" | "error" | "disabled" | "unconfigured";
    query?: string;
    results?: ChatImageSearchResult[];
    error?: string;
    justCompleted?: boolean; // set true only when transitioning from pending->done
  };
  // When present, this "message" is not a chat bubble but a voice-call event
  // marker rendered inline in the thread:
  //   · "started" — a "call with monkey started" separator at the point the
  //                 call began.
  //   · "ended"   — the "voice chat · 2:14" ledger line after a call ends.
  // text_message is empty for these.
  /** `wrap_up`: Tutor calls only, Monkey's one-sentence wrap-up shown after the "voice chat" card. */
  // Tutor only: `filed_class_id` is the class an all-classes call was filed into; a "filed" row in that class's chat
  // links to the call (`call_turn_id`, its first line in the all-classes chat).
  // Tutor only: `topics` is the call's "where you stand" list (post-call review), shown under its card;
  // `review_pending` while that review is still being written (its card waits with a skeleton).
  // Tutor only: `reflection` marks an exam reflection call (its card is labelled "reflection" and shows the learner's
  // takeaway in their own words instead of "where you stand").
  voice_event?: { kind: "started" } | { kind: "ended"; duration_seconds?: number; wrap_up?: string; filed_class_id?: string;
    topics?: { title: string; status: "solid" | "shaky" | "not_yet"; evidence: string }[]; review_pending?: boolean;
    reflection?: { exam_name: string; takeaway: string | null } }
    | { kind: "filed"; call_turn_id: string; duration_seconds?: number };
  // True while a voice transcript bubble is still being streamed in by the
  // agent. Drives the smooth word-by-word reveal animation; cleared on the
  // turn-final delta.
  is_voice_streaming?: boolean;
  voice_citations?: import("../voice/core/voiceSourceCitations").VoiceSourceCitation[];
  // Inline tool-call chips for an agentic assistant turn. The agentic chat
  // endpoint (`/core/agentic-chat`) streams `tool-start` / `tool-result` SSE
  // events as the model invokes tools (web search, image search, document
  // search, URL extraction) mid-answer. Each entry is pushed in stream order
  // on `tool-start` (status `running`) and flipped on the matching
  // `tool-result` (matched by `toolCallId`, status `done`). Only ever present
  // on assistant messages.
  tool_calls?: ChatMessageToolCall[];
  // Ordered, interleaved view of an agentic assistant turn's content. The SSE
  // stream delivers `text-delta`, `tool-start`, and `tool-result` events IN
  // ORDER; flattening them into `text_message` + `tool_calls` + `image_search`
  // loses that order, so an image the model placed mid-explanation ends up
  // dumped at the bottom. `content_parts` preserves stream order: the bubble
  // renders text segments, tool-call chips, and image strips exactly as they
  // arrived. The flat fields above are still populated in parallel (analytics,
  // feedback, and the legacy NDJSON path read them). Only ever present on
  // assistant messages produced by the agentic endpoint.
  content_parts?: ChatMessageContentPart[];
  // Set by an SSE `error` event when an agentic turn fails. `is_error` drives
  // the error-styled bubble; `is_retryable` shows a Retry affordance.
  error_message?: string;
  is_error?: boolean;
  is_retryable?: boolean;
  // The conversation thread is unified across text and voice — see
  // docs/analytics/voice-and-agentic-chat-tracking-plan.md §8. `source` marks
  // which modality produced the message; voice messages also carry
  // `voiceConversationId`, linking to the ai_voice_conversations record.
  // All are optional: ChatMessages stored before this change lack them.
  source?: "text" | "voice";
  voiceConversationId?: string;
  // Voice-call lifecycle markers persisted into the thread: the first and
  // last entries of a voice call carry `voiceCallEvent`, so the call shows
  // its boundaries on refresh. The chat UI keys off `voiceCallEvent` to
  // render the "call started" / "call ended" chips; the "ended" marker also
  // carries `durationSeconds` (raw seconds, for the UI to format).
  voiceCallEvent?: "started" | "ended";
  durationSeconds?: number;
  // Out-of-band visuals rendered during a voice assistant turn. These are
  // attached when the finished voice call is mirrored into `user_messages` so
  // diagrams/tables/formulas/images survive refresh and show in admin review.
  artifacts?: ChatMessageVoiceArtifact[];
  // Voice tool observability events attached to the assistant turn where the
  // tool was called. Admin-only renderers use this; learner chat surfaces do
  // not render it.
  toolEvents?: ChatMessageVoiceToolEvent[];
};

/**
 * One picture from an `image_search` result. The text chat's single picked image also carries its pixel
 * size, alt text, an optional caption and up to three pen callouts (`target` is a box in fractions of the
 * image, top-left origin); with a size it renders framed like the study guide's figures (ChatFigure.tsx).
 * Voice results and older threads have only the first four fields and keep the plain image strip.
 */
export type ChatImageSearchResult = {
  url: string;
  thumbnail?: string;
  title?: string;
  source?: string;
  width?: number;
  height?: number;
  alt?: string;
  caption?: string | null;
  annotations?: ChatImageAnnotation[];
};

export type ChatImageAnnotation = {
  id: string;
  label: string;
  target: { x: number; y: number; width: number; height: number };
};

/** A single agentic tool invocation rendered as an inline chip in the chat. */
export type ChatMessageToolCall = {
  toolCallId: string;
  toolName: string;
  args: Record<string, unknown>;
  output?: Record<string, unknown>;
  status: "running" | "done" | "failed";
};

export type ChatMessageVoiceToolEvent = {
  toolName: string;
  status?: string | null;
  durationMs?: number | null;
  resultCount?: number | null;
  query?: string | null;
  language?: string | null;
  artifactId?: string | null;
  artifactKind?: string | null;
  turnId?: string | null;
  receivedAt?: number | null;
  errorFamily?: string | null;
  errorMessage?: string | null;
  [key: string]: any;
};

export type ChatMessageVoiceArtifact = {
  id: string;
  kind: "diagram" | "table" | "formula" | "image" | "html" | "question";
  diagram?: string;
  table?: string;
  formula?: string;
  image?: {
    image_url?: string;
    thumbnail_url?: string;
    title?: string;
    source_url?: string;
  };
  imageSearch?: {
    query?: string | null;
    language?: string | null;
    durationMs?: number | null;
    resultCount?: number | null;
    status?: string | null;
    candidates?: Array<{
      imageUrl?: string | null;
      thumbnailUrl?: string | null;
      title?: string | null;
      sourceUrl?: string | null;
      sourceDomain?: string | null;
      mime?: string | null;
      fileFormat?: string | null;
      width?: number | null;
      height?: number | null;
      byteSize?: number | null;
      reason?: string | null;
      [key: string]: any;
    }> | null;
    pickedImage?: {
      imageUrl?: string | null;
      thumbnailUrl?: string | null;
      title?: string | null;
      sourceUrl?: string | null;
      [key: string]: any;
    } | null;
    pickedImageUrl?: string | null;
    pickedIndex?: number | null;
    selectionReason?: string | null;
    selectionMethod?: string | null;
    candidatePreviewCount?: number | null;
    candidatePreviewSources?: string[] | null;
    candidatePreviewDetails?: Array<{
      candidateIndex?: number | null;
      source?: string | null;
      byteCount?: number | null;
      mimeType?: string | null;
      width?: number | null;
      height?: number | null;
    }> | null;
    rejectedImageCandidates?: Array<{
      imageUrl?: string | null;
      thumbnailUrl?: string | null;
      title?: string | null;
      sourceUrl?: string | null;
      sourceDomain?: string | null;
      mime?: string | null;
      fileFormat?: string | null;
      width?: number | null;
      height?: number | null;
      byteSize?: number | null;
      reason?: string | null;
      [key: string]: any;
    }> | null;
    rawResultCount?: number | null;
    latencyBreakdownMs?: Record<string, number | null> | null;
    artifactId?: string | null;
    artifactPublished?: boolean | null;
    errorFamily?: string | null;
    errorMessage?: string | null;
    [key: string]: any;
  };
  html?: string;
  htmlSize?: "compact" | "standard" | "tall";
  question?: string;
  caption?: string;
  turnId?: string;
  receivedAt?: number;
  // Content-free diagram provenance. `generatedAt` is used only to derive a
  // coarse age bucket before telemetry is sent; the exact timestamp is never
  // reported. Older persisted artifacts legitimately omit these fields.
  generatedAt?: number;
  generatorVersion?: string;
  generatorRepairKind?: "none" | "safe_flowchart_rebuild";
  generatorRegenerationAttempted?: boolean;
  deliverySource?: "live" | "persisted";
};

export type ChatMessageVisualArtifact = ChatMessageVoiceArtifact | {
  id: string; kind: "drawing"; drawing: string; caption?: string; receivedAt?: number;
} | {
  // `render_flow`: a JSON step chain drawn like the study guide's process chain (ChatFlow.tsx).
  id: string; kind: "flow"; flow: string; caption?: string; receivedAt?: number;
};

/**
 * One ordered segment of an agentic assistant turn's content. The bubble maps
 * over `ChatMessage.content_parts` and renders each part in place:
 *  - `text`   → the `MarkdownWithCitations` renderer (an accumulating chunk of
 *               answer text; consecutive `text-delta`s extend the trailing
 *               text part rather than appending a new one).
 *  - `tool`   → a single `ToolCallChips` chip for the invocation.
 *  - `images` → a `ChatMessageImages` strip (an `image_search` tool-result),
 *               or one framed picture when the result carries its size.
 *  - `artifact` → a rendered diagram/table/formula/drawing/flow artifact.
 */
export type ChatMessageContentPart =
  | { type: "text"; text: string }
  | { type: "tool"; toolCall: ChatMessageToolCall }
  | { type: "images"; imageSearch: NonNullable<ChatMessage["image_search"]> }
  | { type: "artifact"; artifact: ChatMessageVisualArtifact };

export type IconProps = {
  color?: any;
  width?: number;
  height?: number;
  style?: any;
  strokeWidth?: number;
  rotation?: number; // degrees of rotation for SVG icons (optional)
};

export type OcclusionImageWithBoxPositions = {
  imageUrl: string;
  mergedBoxes: Array<{
    index: number;
    labelText: string;
    left: number;
    right: number;
    top: number;
    bottom: number;
  }>;
  originalBoxes: Array<{
    index: number;
    labelText: string;
    left: number;
    right: number;
    top: number;
    bottom: number;
  }>;
  hasBeenEditedByUser: boolean;
  deckId?: string; // this is the id of the deck that the image belongs to, used to identify which deck the image belongs to when editing occlusion boxes
};
export enum LeaderboardPositionZone {
  PROMOTE = "promote",
  STAY = "stay",
  DEMOTE = "demote",
}

export enum Tree {
  Kapok = "Kapok",
  Ipe = "Ipe",
  Banyan = "Banyan",
  Banana = "Banana",
  DragonBlood = "Dragon Blood",
}

// high level character selection, more specific versions are below
export enum AnimalCharacter {
  Monkey = "Monkey",
  Capybara = "Capybara",
  Sloth = "Sloth",
}

// this is so that we know which specific version of the character we are using
export enum AnimalCharacterWithAccessory {
  MonkeyNoAccessory = "Monkey no accessory",
  MonkeyWithGlasses = "Monkey with glasses",
  MonkeyWithBanana = "Monkey with banana",

  CapybaraNoAccessory = "Capybara",
  CapybaraWithGlasses = "Capybara with Glasses",
  CapybaraWithFruit = "Capybara with Fruit",

  SlothNoAccessory = "Sloth",
  SlothWithGlasses = "Sloth with Glasses",
  SlothWithFruit = "Sloth with Fruit",
}

export enum LearningJourneyModule {
  SummaryOutline = "Summary Outline",
  CardsForUnderstandingKeyConcepts = "Cards for Understanding Key Concepts",
  CaseScenarioCards = "Case Scenario Cards",
  ImageOcclusionCards = "Image Occlusion Cards",
  FreeRecall = "Free Recall",
  MultipleChoiceQuestion = "Multiple Choice Question",
  CaseScenarioMultipleChoiceQuestion = "Case Scenario Multiple Choice Questions",
  TrueOrFalseQuestion = "True or False Questions",
}

export enum LearningJourneyCompletedViewState {
  CompletionCelebration = "CompletionCelebration",
  OverviewOfBestTopics = "OverviewOfBestTopics",
  OverviewOfOkayTopics = "OverviewOfOkayTopics",
  OverviewOfTopicsThatNeedImprovement = "OverviewOfTopicsThatNeedImprovement",
  NextStepsNavigationOptions = "NextStepsNavigationOptions",
}

export enum TimeFilterNameToUseForLeaderboard {
  LEADERBOARD_CURRENT_WEEK_FILTER_NAME = "current week",
  LEADERBOARD_CURRENT_MONTH_FILTER_NAME = "current month",
  LEADERBOARD_ALL_TIME_FILTER_NAME = "all time",
}

export type SummaryOutlineObject = {
  page_title: string;
  summary_outline: string;
  page_generation_id: string;
  deck_name: string;
  deck_id: string;
};

export type NoteItem = { type: "summary"; data: SummaryOutlineObject } | { type: "recording"; data: LiveRecording };

export type LastCardUserVisitedInLearningJourneyModuleData = {
  last_card_id_viewed: string;
  last_card_viewed_at: Date;
  last_key_concept_viewed: KeyConcept;
  last_card_index_viewed: number;
};

export type UserInteractionForPageGeneration = {
  has_learning_journey_been_marked_as_completed: boolean;
  understanding_rating?: any;
  last_card_user_visited_for_learning_journey_modules: {
    [key in LearningJourneyModule]?: LastCardUserVisitedInLearningJourneyModuleData;
  }; // this is used to track the progress of the learning journey modules
};

export type UnderstandingRatingsForNonAuthenticatedUser = {
  okayCards?: Record<string, CardData>;
  notSureCards?: Record<string, CardData>;
  iKnowItCards?: Record<string, CardData>;
};

export type ResumeLearningJourneyByModule = {
  totalCards: number;
  lastCardAnswered: CardData | null;
  totalCardsAnswered: number;
};

export type FreeResponseAnswerFromUserWithFeedback = {
  id?: string;
  date_answered?: any;
  answer_from_user?: string;
  is_currently_generating_feedback?: boolean;
  feedback_from_ai?: {
    correct?: string;
    incorrect?: string;
    additional?: string;
    answer_understanding_score?: string;
  };
  success?: boolean;
  num_of_attempts?: number;
  should_generate_feedback_for_answer?: boolean;
};

export type KnowledgeUnderstandingRatingObject = {
  date_rated: any;
  id: string;
  understanding_rating: KnowledgeUnderstandingRating;
  answer_from_user_and_ai_feedback_id?: string;
};

export type CountryAndSchoolsData = {
  code: string;
  name: string;
  universities: string[];
  flag: string;
};

export type UnderstandingRatingsForCardByUser = {
  most_recent_knowledge_understanding_rating?: number;
  most_recent_date_rated?: Date;
  most_recent_answer?: string;
  most_recent_incorrect_answer?: string;
  most_recent_incorrect_answer_feedback?: string;
  understanding_ratings_history_object_list?: {
    date_rated: Date;
    understanding_rating: number;
    spaced_repetition_id_of_knowledge_rating?: string;
  }[];
  most_recent_spaced_repetition_knowledge_rating?: number; // This is the grade used for the daily review, because each day progress must be reset for a new daily review.
  most_recent_spaced_repetition_id_of_knowledge_rating?: string; // this is for save the id of last Day Review was rated
};

export type LectureSource = {
  schema_version: 1;
  recording_id: string;
  upload_task_id: string;
};

export type LectureSourceUploadContext =
  | { lecture_recording_id: string; lecture_source_task_id?: never }
  | { lecture_source_task_id: string; lecture_recording_id?: never };

export type FileUploadWithDeckData = {
  lectureSourceContext?: LectureSourceUploadContext;
  file: File;
  associatedDeck?: DeckObject; // optional deck object associated with the file
  hasUploadError?: boolean; // true if an error occurred during file upload
  isBeingRead?: boolean; // true if the file is currently being read
  uploadErrorMessage?: string; // error message if hasUploadError is true
  uploadedFileIndex?: number; // index of the file in the list of uploaded files
  // The generation this source was last submitted to. Kept beside the source
  // deck so returning to the generate page never mistakes the generated deck
  // for the source (which used to nest a paid duplicate generation under it).
  submittedGeneration?: {
    generatedDeckId: string;
    settingsFingerprint: string | null;
    languagePreference: string | null;
  };
};

export type SourceObject = {
  sourceType: ContentMediumType;
  sourceUrl: string;
  sourceName: string;
  deckId?: string;
};

export type AssessmentUnitStimulusBlock =
  | {
      type: "markdown";
      text: string;
      label?: string;
    }
  | {
      type: "equation";
      latex: string;
      label?: string;
    }
  | {
      type: "table";
      caption: string;
      columns: string[];
      rows: (string[] | { cells: string[] })[];
      footnote?: string | null;
    }
  | {
      type: "source_image";
      asset_id: string;
      asset_url?: string;
      caption: string;
      alt_text: string;
      extraction_confidence: number;
    };

export type CardData = {
  id?: string;
  question: string;
  card_type?: CardType;
  card_topics?: Array<string>;
  answer_from_ai?: string;
  answer?: string; //for multiple choice questions, the 'answer' field is the correct answer
  answer_generated_at_questions_generation_step?: string; // the answer generated at the questions generation step (this answer is used at the question to card type conversion step, we save it here for debugging)
  usual_misconceptions?: string[]; // a list of common misconceptions related to the question (these misconceptions are used at the question to card type conversion step, we save it here for debugging)
  relevant_question_sentences?: string[]; // DEPRECATED : we are using relevant_context_for_question instead now
  relevant_context_for_question?: string;
  location_context?: string; // the most relevant page/minute from which the card was generated.
  location_context_from_llm?: Array<number | string> | number | string; // generation-time source pages/seconds; fallback when retrieval has no locator
  did_attempt_to_get_relevant_context_for_question?: boolean;
  user_id: string;
  card_index: number;
  is_explanation_showing?: boolean; // if we are showing the explanation, we want to sort the answer choices to prioritize the user's answer and the correct answer
  explanation_already_shown?: boolean; // if the user has already seen the explanation
  deck_id_to_card_index?: {
    // used for sorting in a particular order when the user resumes where they left off
    [deckId: string]: number;
  };
  content_medium_type: ContentMediumType;
  date_created: any;
  date_saved?: any;
  flashcard_generated_id: string;
  is_saved: boolean;
  page_domain: string;
  page_title: string;
  article_url?: string; // DEPRECATED around  June 2023, use page_url instead
  page_url?: string;
  deck_name?: string;
  deck_id?: string;
  deck_ids?: string;
  question_side_image_url?: string;
  answer_side_image_url?: string;
  image_url_to_export_to_anki?: string; // this is used for image occlusion cards (we take a screenshot of the image with the occlusion boxes)
  generated_card_id?: string;
  current_user_answer_input?: string;
  card_id?: string;
  anki_card_id?: number;
  has_answer_been_edited?: boolean;
  should_hide_card?: boolean; // currently, a card is hidden if it was not a part of the first chunk of generations and we have not finished detecting and removing duplicates
  is_deleted?: boolean; // DEPRECATED
  card_generation_language?: string;
  should_show_answer_feedback?: boolean;
  is_custom_card?: boolean; // if a card is manually created by the user. if this is true, there will be a lot of fields that wont be used.
  taxonomy_level?: QuestionTaxonomy;
  answer_choices_prompt_version?: string; // we run prompt experiments so this tells us which version of the prompt was used to generate the answer choices
  was_generated_with_new_pipeline?: boolean; // new question generation architecture launch on 5/2/2025
  question_generation_worker?: string;
  question_generation_template_id?: QuestionGenerationTemplateId | string;
  langsmith_trace_id?: string; // the trace id of the langsmith trace for the card (only relevant for cards generated with the new pipeline)
  langsmith_trace_run_id?: string; // child run used to correlate per-card quality feedback in LangSmith
  // question importance score
  question_importance_score?: number;
  question_relevance_score?: number;

  // occlusion properties
  occlusion_label_number?: number;
  occlusion_label_index?: number;
  occlusion_labels?: any[];

  // case scenario properties
  case_scenario_details?: string;
  exam_tags?: Record<string, unknown>;
  assessment_unit_id?: string;
  assessment_unit_kind?: "independent" | "single_stimulus" | "shared_stimulus";
  assessment_unit_question_id?: string;
  assessment_unit_question_index?: number;
  assessment_unit_question_count?: number;
  assessment_unit_stimulus_schema_version?: number;
  assessment_unit_stimulus?: AssessmentUnitStimulusBlock[];
  assessment_unit_stimulus_title?: string;
  generated_explanation?: string;
  generated_distractor_feedback_by_answer?: Record<string, string>;
  assessment_unit_stimulus_override_markdown?: string;
  assessment_unit_exam_tags?: Record<string, unknown>;

  weaviate_document_id?: string; // DEPRECATED (we no longer use weaviate): weaviate contains embedding vectors of the document
  did_document_get_vectorized?: boolean; // embedded into pinecone db

  // learning journey properties
  card_key_concept_name?: string;

  // for multiple choice cards
  distractor_answers_for_multiple_choice_question?: string[];
  mcq_ai_feedback_for_answers?: string | null; // contains the ai generated feedback for all mcq answer choices
  mcq_more_info_feedback_for_correct_answer?: string; // DEPRECATED: storing the feedback we generate so we can show it instantly to the user
  mcq_incorrect_answer_to_more_info_feedback_mapping?: { [key: string]: string }; // DEPRECATED
  selectedAnswerText?: string;
  selectedAnswerLetter?: string; // The index of the answer choice that the user selected can be A, B, C, D
  correctAnswerLetter?: string; // The index of the correct answer choice can be A, B, C, D
  answerChoices?: string[];
  moreInfoFeedbackForMultipleChoiceAnswer?: string;
  correctAnswerMessage?: string;

  // answers and rating
  knowledge_understanding_ratings?: Array<KnowledgeUnderstandingRatingObject>;
  answers_from_user_and_ai_feedback?: Array<FreeResponseAnswerFromUserWithFeedback>;
  answer_from_user_and_ai_feedback?: any;
  understanding_ratings_for_topics_by_user?: any; // temporarily adding this because julian did migration before we fixed this field name
  understanding_ratings_for_card_by_user?: Record<string, UnderstandingRatingsForCardByUser>;

  // this information gets generated only when the user got a question wrong and then they press the "review recent mistakes" button in the checkpoint
  additional_info_to_understand_question_better?: {
    analogy_text?: string;
    what_you_need_to_know_bullet_points?: string[];
    created_at?: Date;
    did_analogy_fail_to_generate?: boolean;
    did_fail_to_generate_what_you_need_to_know_bullet_points?: boolean;
  };
  user_messages?: Record<string, Array<ChatMessage> | null>;

  // rephrasing properties
  rephrased_question_versions?: Array<{
    question: string;
    answer: string;
    distractor_answers_for_multiple_choice_question?: string[];
    date_last_answered_by_user: Record<string, Date | FirebaseDate>;
    created_at: Date | FirebaseDate; // the date the rephrased question was created
  }>;
  is_question_being_rephrased?: boolean;
  displayedVersionIndex?: number; // tracks which version was shown to user before answering (frontend only)

  // fields to track which version is being displayed (original or rephrased version)
  // these are state only fields, they are not stored in the database
  questionVersionBeingUsed?: string; // The actual question text being displayed
  answerVersionBeingUsed?: string; // The actual answer text being displayed
  distractorAnswersVersionBeingUsed?: string[]; // The actual distractor answers being displayed
  rephrasedQuestionVersionIndexBeingUsed?: number; // -1 for original, 0+ for rephrased versions
};

export type PDFImage = {
  imageUrl: string;
  fileName: string;
  page_number?: number;
  has_been_selected?: boolean; // this is used to track which images the user has selected for image occlusion
};

export type QuestionGenerationLifecycleStatus =
  | "accepted"
  | "orchestrating"
  | "dispatched"
  | "running"
  | "first_content"
  | "success"
  | "partial_success"
  | "failed"
  | "cancelled";

export type QuestionGenerationLifecycle = {
  schema_version?: number;
  status?: QuestionGenerationLifecycleStatus;
  stage?: string;
  stage_sequence?: number;
  pipeline?: string;
  operational_state?: string;
  heartbeat_at?: unknown;
  last_progress_at?: unknown;
  request_accepted_at?: unknown;
  orchestration_dispatched_at?: unknown;
  backend_orchestration_started_at?: unknown;
  modal_dispatch_started_at?: unknown;
  modal_dispatched_at?: unknown;
  worker_started_at?: unknown;
  planning_started_at?: unknown;
  first_concept_persisted_at?: unknown;
  first_question_persisted_at?: unknown;
  first_content_persisted_at?: unknown;
  completed_at?: unknown;
  modal_worker_queue_delay_ms?: number;
  time_to_first_content_ms?: number;
  total_duration_ms?: number;
  questions_persisted?: number;
  concepts_persisted?: number;
  expected_bucket_count?: number;
  completed_bucket_count?: number;
};

export type DeckObject = {
  number_of_flashcards_generated?: number;
  id?: string | null;
  is_active?: boolean;
  is_deleting?: boolean;
  is_private?: boolean;
  name?: string | null;
  color?: string;
  // A root deck's chosen icon and color. Any client can write a deck, so this is loose on purpose: read it only
  // through parseClassAppearanceOverride (src/features/tutor/tutorClassAppearance.ts).
  class_appearance?: { icon?: unknown; color?: unknown };
  cards_count?: number;
  card_id_to_last_understanding_rating_mapping?: { [key: string]: string };
  date_created: any;
  user_id?: string;
  last_card_saved_to_deck_date?: Date;
  last_card_id_saved_to_deck?: string;
  children_deck_ids?: string[];
  children_decks?: DeckObject[];
  parent_deck?: DeckObject | null;
  parent_deck_id?: string | null;
  parent_decks?: DeckObject[]; // parent_decks is a calculated value that gets the chain of ancestors (parent & parents of parents) for the current deck.
  last_date_viewed?: Date; // last date the deck overview was visited
  last_date_reviewed?: Date; // last date the deck was reviewed
  is_deck_the_first_deck_generated_by_user?: boolean;
  cards_count_including_subdecks?: number;
  should_skip_show_confirmation_for_delete_card?: boolean; // this is used to determine if the user has seen the confirmation modal for deleting a card
  duplicate_deck_from_deck_id?: string;
  last_card_answered_index_by_user?: {
    [userId: string]: number;
  };

  // live recordings saved to this deck (optional)
  live_recording_ids?: string[];

  // properties for frontend only
  userThatCreatedDeck?: UserData;

  // jungle trees properties
  deck_progress_scores_and_animation_state_by_user?: {
    [userId: string]: {
      current_deck_progress_score?: number;
      max_deck_progress_score?: number;
      has_fixed_deck_progress_score?: boolean; // Esther added this temporarily to fix decks that store incorrect mastery progress scores before all the cards are generated
      lastest_answer_date: string; // it's not a date, because we convert to ISO string
    };
  };

  // properties for if the deck is a class. a deck is a class if it was shared and at least 1 person joined the class
  is_deck_a_class?: boolean;
  class_properties?: {
    ids_of_users_in_class?: string[];
    users_in_class?: UserData[];
  };

  // properties related to exams
  ids_of_exams_related_to_this_deck?: string[];

  // ---------------------------------------------
  // For decks that are generated from a document
  // ---------------------------------------------
  is_deck_generated_from_document?: boolean;
  generation_metadata?: {
    landing_handoff_context?: import("../pages/home-page/landingHandoffContext").LandingHandoffContext;
    is_from_live_recording?: boolean;
    lecture_source?: LectureSource;
    did_generation_succeed?: boolean;
    page_url?: string;
    page_title?: string; // for a PDF, the pageTitle will be the name of the PDF file
    content_medium_type?: ContentMediumType | null;
    page_text?: string | null; // for a PDF, pageText will only have a value once a range of pages is picked
    page_text_sentences_array?: any[] | null; // the array of sentences in pageText
    page_domain?: string;
    should_run_generations_with_new_architecture?: boolean;
    question_generation_worker?: string;
    question_generation_attempt_id?: string;
    orchestration_modal_function_call_id?: string;
    modal_function_call_id?: string;
    question_generation_lifecycle?: QuestionGenerationLifecycle;
    // Where a failed generation stopped (worker_generation, result_delivery, generation_watchdog, …), written by
    // the backend when it marks the attempt failed
    failure_stage?: string;
    failure_type?: string;
    question_generation_template_id?: QuestionGenerationTemplateId | string;
    include_table_mcqs?: boolean;
    atomic_staging_status?: string;
    atomic_concepts_ready?: boolean;
    is_summary_outline_generation_in_progress?: boolean;
    atomic_staging_validated_question_count?: number;
    atomic_staging_preview?: {
      assessment_unit_id?: string;
      assessment_unit_payload_encoding?: string;
      assessment_unit_payload_json?: string;
      bucket_id?: number;
      unit_sequence_index?: number;
    };
    question_types_user_selected_to_generate?: CardTypeWithDifficulty[]; //types of questions user selected to generate
    langsmith_trace_id?: string; // the trace id of the langsmith trace for the deck (only relevant for decks generated with the new pipeline)
    level_for_amount_of_cards_to_generate?: LevelForAmountOfCardsToGenerate; // the level of the deck for the amount of cards to generate, this is used to determine how many cards to generate for the deck

    // uploaded file properties
    get_data_from_file_processing_status_doc_id?: string; // the id of the document that contains the status of the file processing & the text extracted from the filea
    study_guide_source_receipt_id?: string;
    uploaded_file_s3_bucket_name?: string;
    uploaded_file_s3_object_key?: string;
    occlusion_images_with_box_positions_before_user_edits?: Array<OcclusionImageWithBoxPositions>;

    // loading states
    active_number_of_question_generation_requests?: number;
    is_processing_occlusion_images?: boolean; // this is for the processing needed to drawing the occlusion boxes on the selected images
    is_generating_image_occlusion_cards_for_image?: boolean; // this is for the generation of image occlusion cards
    is_page_generation_in_progress?: boolean;
    is_currently_generating_knowledge_tree?: boolean;
    is_currently_generating_case_concept_cards?: boolean;
    is_currently_generating_key_concept_cards?: boolean;
    is_currently_generating_multiple_choice_questions?: boolean;
    did_summary_outline_generations_for_all_concepts_get_triggered?: boolean;
    did_case_questions_generations_for_all_concepts_get_triggered?: boolean;
    is_currently_generating_multiple_choice_case_questions?: boolean;

    // pdf only properties
    pdf_starting_page?: number;
    pdf_ending_page?: number;
    pdf_num_pages?: number;
    pdf_pages_text_array?: string[];
    pdf_file_name?: string;
    pdf_images_object_list_doc_id?: string | null;
    pdf_images_object_list?: Array<PDFImage>;
    pdf_url_for_file_upload?: any;

    // video / audio / youtube properties
    video_or_audio_num_minutes?: number | undefined;
    video_transcript_text_and_timestamps_array?: Array<{
      duration?: number;
      start: number;
      end?: number;
      text: string;
    }>;

    // youtube only properties -- TODO: refactor to use video_or_audio_starting_minute
    youtube_transcript_start_minute?: number | 0;
    youtube_transcript_end_minute?: number;

    // video / audio only properties
    video_or_audio_starting_minute?: number;
    video_or_audio_ending_minute?: number;

    // summary outline properties
    is_currently_generating_summary_outline?: boolean;
    summary_outline?: string;

    // pinecone properties
    pinecone_storage_index_name?: string; // this is deprecated
    pinecone_namespace_for_non_authenticated_user?: string; // this is deprecated

    turbopuffer_namespace_for_non_authenticated_user?: string;
    did_document_get_vectorized?: boolean; // embedded into pinecone db. this is deprecated
    is_vectorized_on_turbopuffer?: boolean; // indicates if the document has been vectorized on turbopuffer

    page_text_hash?: string | null; // a hash of the value of the text that was used to generate
    page_text_language?: string; // the language of the text on the page
    has_been_generated_from_multiple_documents?: boolean; // this is true if the deck was generated from multiple documents, for example, a PDF with multiple pages or a video with multiple minutes

    question_types_user_select_to_generate?: CardTypeWithDifficulty[]; // the types of questions the user selected to generate for this deck
    standard_question_types_user_select_to_generate_before_template_switch?: CardTypeWithDifficulty[];
    clinical_exam_ids?: string[]; // ids from src/clinical-exams/clinicalExamCatalog.ts that the user chose to practice (at most one since 2026-10-01: the examination that fits the document best); undefined until the document has been checked for a match
    clinical_exam_practice_turned_off?: boolean; // the user unchecked the "OSCE / mock clinical examination" row. Kept apart from clinical_exam_ids so the examination can change with the page range without the choice resetting
    clinical_exam_ids_turned_off?: string[]; // before 2026-10-01 the row was one checkbox per examination and this held the ids the user unchecked; only read, to carry such an opt-out over
    suggested_exam_type?: string; // optional free-text exam the user is studying for (e.g. "USMLE Step 1", "NBME Shelf"), used to steer question generation
  };
  // for chat with document history
  user_messages?: Record<string, Array<ChatMessage> | null>;
};

export type UserTimeCapsuleObject = {
  user_id: string;
  message: string;
  created_at: Date;
  open_at: Date | null;
  opened_at: Date | null;
  was_opened: boolean;
};
export type ExamObject = {
  // Identifiers only; null records completion and supersedes legacy local storage.
  review_bookmark?: { cardIds: string[]; currentCardId: string } | null;
  // Incremented on completion so an older device cannot replay a finished review.
  review_bookmark_completion_version?: number;
  review_bookmark_write?: { writerId: string; sequence: number };
  id: string;
  user_id: string;
  exam_name: string;
  created_at: Date;
  date_of_exam: Date | FirebaseDate;
  updated_at: Date;
  is_exam_completed?: boolean;
  is_deleted?: boolean;
  /** Canonical Tutor exams are edited through the versioned exam API. */
  tutor_schema_version?: number;
  class_deck_id_that_this_exam_belongs_to?: string; // the deck id of the class that this exam belongs to
  ids_of_decks_related_to_this_exam?: string[]; // the deck ids of the decks that are related to this exam. this is for all the materials / decks that belong to an exam
  class_name?: string | null; // the name of the class this exam belongs to
  deck_name_of_class_to_add_exam_to?: string | null; // the class's name when the exam was saved (written by the Worker)
  //we have a collection of badges for number of exams survived. the number of exams survived goes up when the exam has passed (based on date of exam)
  has_exam_been_counted_for_exams_survived_badges?: boolean;
};

export type LeaderboardData = {
  user_id: string;
  user_name: string;
  character_team_selected: AnimalCharacter;
  character_with_accessory_selected: AnimalCharacterWithAccessory;
  current_week_xp_earned: number;
  current_month_xp_earned: number;
  weekly_xp_earned_history: Record<string, number>;
  monthly_xp_earned_history: Record<string, number>;
  all_time_xp_earned: number;
  last_updated_week_key_name: string;
  last_updated_month_key_name: string;
  university_name?: string;
  country_code?: string;
  ranking: number;

  // DEPRECATED
  animal_avatar_selected?: string;
  animal_team_selected?: string;
};

export type StreakHistory = {
  start_date: Timestamp;
  end_date: Timestamp; // current streak will have end_date set to the current date, even if the streak is still active
  streak_length: number;
  dates_when_streak_saves_were_used: Array<Timestamp>;
};

export type DailyQuestionHistory = {
  date: Timestamp;
  total_questions_answered: number;
  correct_answers: number;
  incorrect_answers: number;
  accuracy_percentage: number;
  card_types_breakdown?: {
    [cardType: string]: {
      total: number;
      correct: number;
    };
  };
};

export type StreakData = {
  user_id: number;
  number_of_days_for_current_streak: number;
  number_of_days_for_longest_streak: number;
  last_date_streak_was_updated: Date; // the last date the user answer for than 10 questions and received a streak increase
  number_of_questions_answered_today: number; // if the user has answered more than 10 questions in a day, we don't need to update the database to save DB writes
  user_timezone: string;
  should_show_streak_increased_page: boolean; //we only show this page once either on the checkpoint or in the finish screen
  last_date_streak_daily_clean_up_was_run: Timestamp;

  // History of streak periods, including the current streak
  streak_history: Array<StreakHistory>;
  num_of_streak_saves_left: number; // cached balance for the current local calendar month
  streak_freeze_allowance_month?: string; // YYYY-MM in user_timezone
  streak_freeze_policy?: "monthly_v1";
};

export type KeyConcept = {
  id: string;
  key_concept_name: string;
  deck_id: string;
  topics_related_to_concept_list: string[];
  has_user_answered_a_question: boolean; // this is used to determine if the user has completed verification of the cards for this key concept
  index: number;
  is_generating_key_concept_cards_from_concept: boolean;
  has_finished_generating_key_concept_cards_for_concept?: boolean;
  relevant_context_for_concept_and_topics_str?: string;
};

export type CardsDataGroupedByConcept = {
  [keyConcept: string]: CardData[];
};

export const enum FreeRecallInputMethod {
  audio = "Audio",
  video = "Video",
  text = "Text",
}

export enum FreeRecallView {
  ModeSelection = "modeSelection",
  RecordVideo = "recordVideo",
  RecordAudio = "recordAudio",
  FreeRecallFeedback = "freeRecallFeedback",
}

export type FreeRecallAttemptData = {
  created_at: Date;
  key_concept_name: string;
  relevant_topics_list: string[];
  input_method: FreeRecallInputMethod;
  feedback_from_ai: {
    didnt_include: string[];
    incorrect: string[];
    understanding_ratings_for_topic: { [topic: string]: number };
  };
  input_text?: string;
  file_name?: string;
  is_ai_feedback_currently_generating: boolean;
  is_transcript_currently_generating?: boolean;
  transcript_text?: string | null;
};

export enum FreeRecallRecordingState {
  Recording = "recording",
  Finished = "finished",
  NotStarted = "notStarted",
  Submitted = "submitted",
}

export enum LeaderboardLeagues {
  SPROUT = "Sprout",
  LEAF = "Leaf",
  FERN = "Fern",
  IVY = "Ivy",
  LILY_PAD = "Lily pad",
  SILVERLEAF = "Silverleaf",
  GOLDENLEAF = "Goldenleaf",
  CELESTIAL_LEAF = "Celestial Leaf",
}

export enum UserKeyFeatureTried {
  LIVE_RECORDINGS = "live_recordings",
  IMAGE_OCCLUSION = "image_occlusion",
  SUMMARY_OUTLINE = "summary_outline",
  ADD_CARD_MANUALLY = "add_card_manually",
}
export enum ManagerSubscriptionAccount {
  Apple = "apple",
  Google = "google",
  Stripe = "stripe",
  // A one-time exam pass bought through Stripe: Elite until a fixed date, never renews (Worker docs/exam-pass.md)
  StripeExamPass = "stripe_exam_pass",
}

export type SummaryHighlight = {
  bullet_points: string[];
};

export type KnowledgeRatingCardsArray = {
  okayCardsArray?: CardData[];
  iKnowItCardsArray?: CardData[];
  notSureCardsArray?: CardData[];
  skippedCardsArray?: CardData[];
};

// this data is only saved if a generation is complete
// id = same id used in the document in the generation_data_for_pages collection
export type PageGeneratedDataToSave = {
  page_url: string;
  domain_url: string;
  date_generated: Date;
  page_text_character_count: number;
  questions_and_answers: { question: string; answer: string }[];
  summary_highlights: SummaryHighlight[];
  content_medium_type: ContentMediumType;
};

// id = page_url
export type PageGenerationStateDataToSave = {
  is_page_generation_in_progress: boolean;
  last_text_chunk_index_used_for_generation: number;
  page_url: string;
  domain_url: string;
  date_generated: Date;
  page_text_character_count: number; // this is used to check if the page text has changed since the last generation (the text could be different for the same url if it is a digital book)
  questions_and_answers: { question: string; answer: string }[];
  summary_highlights: SummaryHighlight[];
  content_medium_type: ContentMediumType;
};

export type ArticleToSaveObject = {
  page_url: string | null;
  questions_and_answers: any[];
  date_saved: Date;
};

export const enum UserSubscriptionTimePeriod {
  MONTHLY = "monthly",
  ANNUAL = "annual",
}

type UserSubscriptionInformation = {
  subscription_status: null | SubscriptionStatus;
  // free plan
  have_updated_num_of_answer_feedbacks_remaining?: boolean;
  num_of_answer_feedbacks_remaining?: number; // this currently resets after 1 hour if you get to 0
  num_of_chat_messages_remaining?: number; // this currently resets after 1 hour if you get to 0
  last_date_answer_feedbacks_ran_out?: FirebaseDate | Date; // this will only be set when the user has used their last feedback
  last_date_chat_messages_ran_out?: FirebaseDate | Date; // this will only be set when the user has used their last chat message
  num_of_advanced_question_generations_left: number;
  // Templated (e.g. USMLE Step 1) question generations — 1/month for every plan
  // below Elite; Elite is unlimited. Mirrors the advanced-questions counter.
  num_of_templated_question_generations_left?: number;
  num_of_monthly_generations_left: number;
  num_of_ai_image_occlusions_left: number;
  num_of_monthly_live_recordings_left?: number; // 5 for free, unlimited for paid
  // Weekly free voice-tutor quota (5 min/week for non-Elite). Written server-side by
  // the /voice/record-usage worker endpoint (the client times the call and reports
  // elapsed seconds on end); read on the client to gate calls + the auto-opened
  // upsell modal. Lives here with the other usage limits, but the reset clock is
  // anchored at EXHAUSTION: when the allotment runs out the server stamps
  // week_start_at and it refills 7 days later (not a calendar-week reset).
  ai_voice_quota_information?: VoiceQuotaInformation;
  last_date_monthly_generations_were_reset?: Date | FirebaseDate; // this date  will get updated every time generations were reset so we know when to reset them again

  // paid plan
  subscription_plan_type: UserSubscriptionPlan | null;
  subscription_period_type?: "annual" | "monthly" | null;
  first_time_subscription_activated_date?: FirebaseDate | Date;
  last_time_subscription_renewed_date?: FirebaseDate | Date; // will only changes if the "subscription_status" is canceled or null
  num_of_renewals?: number; // the number of times the user has renewed their subscription

  // subscription price data, used specifically for ads tracking
  subscription_price_of_last_payment_in_original_currency?: number;
  upcoming_invoice_amount_in_original_currency?: number; // this is the amount that will be charged on the next invoice
  upcoming_invoice_price_currency?: string | null; // currency of upcoming_invoice_amount_in_original_currency (written by the worker)
  subscription_price_of_last_payment_in_usd?: number;
  subscription_price_currency_of_last_payment?: string;

  // subscription purchase metadata
  subscription_id?: string; // this is the id of the subscription on stripe (maybe other sales channels too)
  manager_subscription_account?: ManagerSubscriptionAccount; // this is the account that is managing the subscription
  // Written by the Worker for a one-time exam pass (manager "stripe_exam_pass"); current_period_end_date is its end
  exam_pass?: {
    tier?: "short" | "two_weeks" | "month";
    status?: "active" | "expired" | "unused_has_subscription";
    expires_at?: any;
  } | null;
  stripe_subscription_status?: StripeSubscriptionStatus; // this is the status of the subscription on stripe
  subscription_pause_requested?: boolean;
  subscription_resume_pending_payment?: boolean;
  subscription_pause_paid_period_verification_required?: boolean;
  creator_comped_elite?: boolean;
  creator_comped_paid_subscription_active?: boolean;
  subscription_pause_effective_at?: any;
  paused_subscription_plan_type?: UserSubscriptionPlan | null;
  subscription_resumes_at_date?: any; // this is the date the subscription will resume if it is paused};
  current_period_end_date?: any; // this is the date the current billing period ends
  subscription_next_billing_at_date?: FirebaseDate | Date | string;
  will_subscription_get_canceled_at_period_end?: boolean; // if true, the subscription will be canceled at the end of the billing period
  cancel_subscription_at_date?: any; // this is the date the subscription will be canceled
  has_paid_conversion_been_tracked_on_frontend?: boolean; // tracking on web frontend
  is_pending_stripe_mobile_purchase_tracking?: boolean; // this is used to track if the paid conversion has been tracked through Adjust on mobile app

  // other data
  number_of_referred_users?: number;
  applied_discount_id?: string; // this is the id of the discount that was applied to the subscription
  did_get_grandfathered_from_mega_mind_to_super_learner?: boolean; // this is true if the user was migrated from mega mind to super learner
};

export enum SubscriptionStatus {
  ACTIVE = "active",
  CANCELLATION_PENDING = "cancellation_pending", // legacy display-only state; canonical paid cancellation stays active until paid expiry
  CANCELED = "canceled", // user canceled and the billing period has ended
  PAUSED = "paused", // user paused their subscription
}

export enum StripeSubscriptionStatus {
  INCOMPLETE = "incomplete",
  INCOMPLETE_EXPIRED = "incomplete_expired",
  TRIALING = "trialing",
  ACTIVE = "active",
  PAST_DUE = "past_due",
  CANCELED = "canceled",
  UNPAID = "unpaid",
  PAUSED = "paused",
}

export enum ReviewRemindersStatus {
  ACTIVE = "active",
  PAUSED = "paused",
}

export const enum UserSubscriptionPlan {
  FREE = "Free", // not stored on DB — derived from absence of active subscription
  /**
   * @deprecated Old paid tier replaced by SUPER_LEARNER. No new sales since the
   * MegaMind → Super Learner migration. Some grandfathered users still have this
   * set on subscription_information.plan in Firestore. Do not delete this enum
   * value until the migration script migrate_remaining_mega_mind_users_to_super_learner.py
   * has run in PR 2 and confirmed zero users remain on this plan.
   */
  MEGA_MIND = "Mega Mind",
  /**
   * Internal identifier for what is shown to users as "Pro" on the frontend.
   * The enum value "Super Learner" is preserved unchanged because it is the
   * canonical value written to user.subscription_information.plan in Firestore,
   * used in Stripe product metadata, embedded in URL params
   * (subscription_plan_type=super_learner), and referenced in historical
   * analytics events. To render the display label, use
   * getDisplayNameForSubscriptionPlan() — never compare against the literal
   * string "Pro" anywhere.
   */
  SUPER_LEARNER = "Super Learner",
  /**
   * Higher-tier premium plan. Display label is resolved per user.student_type
   * via getDisplayNameForSubscriptionPlan(): "Medical" for Medical / PreMed
   * students, "Dental" / "Nursing" / "Veterinary" / "Pharmacy" for the matching
   * student types, and "Elite" for everyone else (including non-students and
   * OtherHealthRelated). Features that gate on this tier (voice mode, Step 1
   * templates, better AI model) ship in follow-up PRs; in PR 1 the backend
   * entitlements are identical to SUPER_LEARNER.
   */
  ELITE = "Elite",
}

// One examination's entry in `UserData.voice_clinical_exam_station_progress`:
// the station the learner sat most recently.
export type ClinicalExamStationProgressOnUser = {
  lastScorePercent?: number;
  lastCoveredStepCount?: number;
  lastTotalStepCount?: number;
  lastAttemptedAt?: number; // milliseconds
};

export enum ReasonForShowingSubscriptionUpsell {
  ExportCardsToAnki = "Export Cards to Anki",
  AiImageOcclusion = "Ai Image Occlusion",
  MonthlyRecordingLimitReached = "Monthly Recording Limit Reached",
  LiveRecordingFeature = "Live Recording Feature",
  // Voice tutor free weekly usage (5 min/week for Free + Pro) was exhausted.
  // This is an Elite-ONLY trigger: only Elite grants unlimited voice, so the
  // upsell it opens skips the plan picker and goes straight to the Elite plan
  // (Pro is never shown for this reason). See voice free-quota upsell design.
  VoiceCallFreeUsageLimitReached = "Voice Call Free Usage Limit Reached",
  // Step-1 style templated question generations (1/month below Elite) were
  // exhausted, or the user tapped the "(X free remaining)" info next to the
  // question-style template selector. Elite-tier feature: the upsell opens
  // directly on the plan picker with the Elite card highlighted.
  Step1QuestionTemplates = "Step 1 Question Templates",
  // A Free or Pro user pressed the daily review's +/− size control. Choosing
  // the daily review size is Elite-only, so the upsell opens on the plan picker
  // with the Elite card highlighted and that feature row listed first.
  DailyReviewSize = "Daily Review Size",
  // A Free or Pro user who has used their one free clinical exam station
  // (OSCE practice) tried to open another. Stations are Elite-only, so the
  // upsell opens on the plan picker with the Elite card highlighted and that
  // feature row listed first.
  ClinicalExamPractice = "Clinical Exam Practice",
}

// The context the paywall is opened with after a learner's first review (the "great work" page). It is also the
// `context` value analytics already reports for that paywall, so it must not change. The paywall reads it to
// lead with its own headline there.
export const CONTEXT_FOR_SUBSCRIPTION_UPSELL_AFTER_FIRST_REVIEW = "showed after mini review completed";

export const enum UserEmailAndNotificationPreferences {
  allEmails = "all_emails",
  weeklyReviewEmails = "weekly_review_emails",
  productUpdateEmails = "product_update_emails",
  reviewReminderEmails = "review_reminder_emails",
  noEmails = "no_emails",
}

export type UserNotificationPreferences = {
  review_reminder_on_mobile: boolean;
  review_reminder_on_desktop: boolean;
};

export const enum DaysOfTheWeek {
  Sunday = "Sunday",
  Monday = "Monday",
  Tuesday = "Tuesday",
  Wednesday = "Wednesday",
  Thursday = "Thursday",
  Friday = "Friday",
  Saturday = "Saturday",
}

export enum TecDeMonterreyProgramType {
  AMC = "AMC",
  ARQ = "ARQ",
  BGB = "BGB",
  CIS = "CIS",
  ESC = "ESC",
  IAG = "IAG",
  IAL = "IAL",
  IBQ = "IBQ",
  IBT = "IBT",
  IC = "IC",
  ICI = "ICI",
  ICT = "ICT",
  IDM = "IDM",
  IDS = "IDS",
  IE = "IE",
  IFI = "IFI",
  IID = "IID",
  IIS = "IIS",
  IIT = "IIT",
  IM = "IM",
  IMD = "IMD",
  IMT = "IMT",
  INA = "INA",
  IQ = "IQ",
  IRS = "IRS",
  ITC = "ITC",
  ITD = "ITD",
  LAD = "LAD",
  LAET = "LAET",
  LAF = "LAF",
  LBC = "LBC",
  LC = "LC",
  LCPF = "LCPF",
  LDE = "LDE",
  LDI = "LDI",
  LDO = "LDO",
  LEC = "LEC",
  LED = "LED",
  LEI = "LEI",
  LEM = "LEM",
  LIN = "LIN",
  LIT = "LIT",
  LLE = "LLE",
  LNB = "LNB",
  LPE = "LPE",
  LPS = "LPS",
  LRI = "LRI",
  LTM = "LTM",
  LTP = "LTP",
  LUB = "LUB",
  MC = "MC",
  MO = "MO",
  NEG = "NEG",
  SLD = "SLD",
}
export enum ComunidadLifeType {
  Pasio = "Pasio",
  Reflekto = "Reflekto",
  Ekvilibro = "Ekvilibro",
  Forta = "Forta",
  Energio = "Energio",
  Kresko = "Kresko",
  Spirita = "Spirita",
  Talenta = "Talenta",
  Revo = "Revo",
  Krei = "Krei",
}

export enum UnreleasedFeatureToGetFeedbackOn {
  OverviewOfKeyConcepts = "Overview of key concepts",
}

export enum NewFeaturesForWhatsNewModal {
  ChatWithDocument = "ChatWithDocument",
  NameChangeToJungle = "NameChangeToJungle",
  LeaderboardLeagues = "LeaderboardLeagues",
  AppStoreDownloadPopUp = "AppStoreDownloadPopUp",
  WhereUserCameFromPopUp = "WhereUserCameFromPopUp",
  ChangesToGenerationLimits = "ChangesToGenerationLimits", // nov 23, 2024
  WidgetSetup = "WidgetSetup",
  BadgesAndTimeCapsuleLetterFeature = "BadgesAndTimeCapsuleLetterFeature",
  StudyGuide = "StudyGuide",
}

export enum GroupQuestionTypeBy {
  QuestionType = "question type",
  Topic = "topic",
}

// Specifies the type of view to display in the SidePanelWithTabsComponent.
// Legacy enum - removed in favor of SidePanelTab
// Keeping for reference during refactoring:
// - ChatConversationView -> SidePanelTab.CHAT
// - SourceReferenceView -> SidePanelTab.SOURCE_CONTENT_VIEWER (PDF)
// - SourceYoutubeView -> SidePanelTab.SOURCE_CONTENT_VIEWER (YouTube)

// Modern enum for side panel tabs
export enum SidePanelTab {
  CHAT = "chat",
  SOURCE_CONTENT_VIEWER = "source-content-viewer",
  LIVE_RECORDING_TRANSCRIPT = "live-recording-transcript",
}

// Indicates where/why the side panel is being opened
export enum SidePanelLocation {
  DECK_REVIEW = "deck-review", // Opened during card/deck review
  LIVE_RECORDING = "live-recording", // Opened during live recording session
}

export enum ChatLocations {
  CARD_QUESTION_SIDE = "Card Question Side",
  FREE_RESPONSE_QUESTION_ANSWER_SIDE = "Free Response Question Answer Side",
  CARD_MENU_DROP_DOWN = "Card Menu Drop Down",
  DECK_OVERVIEW = "Deck Overview",
  VIEW_ALL_QUESTIONS_LIST_VIEW = "View All Questions List View",
  SUMMARY_OUTLINE = "Summary Outline",
  REVIEWING_MISTAKES = "Reviewing Mistakes",
  FREE_RECALL = "Free Recall",
  LIVE_RECORDING = "Live Recording",
}

export type FirebaseDate = { seconds: number; nanoseconds: number };

export type UserData = {
  id?: string;
  pending_referral_code?: string | null;
  email_auth: string; // email from google authentication
  email_communication: string; // email that the user wants to receive emails at (only different from email_auth if they manually change it)
  authentication_provider?: "Email" | "Google" | "Microsoft" | "Apple";
  name?: string | null;
  first_name?: string;
  last_name?: string;
  date_created: FirebaseDate | Date; // this is often in the type of an object with seconds and nanoseconds
  timezone?: string;
  date_of_last_visit: FirebaseDate | Date;
  date_of_last_question_answered?: FirebaseDate | Date;
  date_of_last_chat_message_sent?: FirebaseDate | Date;
  subscription_information: UserSubscriptionInformation;
  user_type: UserType | null;
  student_type: StudentType | null;
  articles_saved?: any;
  cards_saved?: any;
  current_spaced_repetition_review?: {
    id: string;
    is_review_currently_being_created?: boolean;
    did_review_creation_fail?: boolean;
    review_status: SpacedRepetitionReviewStatus;
    date_created: any;
    was_review_counted_for_badges?: boolean | null;
  };
  how_did_user_find_us: null | {
    // this is what the user selected from the popup modal
    where_user_came_from_option?: {
      sourceCategoryName: string;
      exactSourceName: string;
    };
    // these sources are all automatically captured
    user_referrer_id?: string;
    // "iphone" | "browser" | "android": the device the referrer shared their link from (its `s=`)
    user_referrer_sharer_device?: string;
    did_user_come_from_meta_ad?: boolean;
    google_campaign_id?: string;
    tiktok_campaign_id?: string;
    tiktok_click_id?: string;
    reddit_campaign_id?: string;
    influencer_campaign_id?: string;
    newsletter_campaign_id?: string;
    id_of_ambassador_that_referred_user?: string;
    utm_source?: string;
  };
  // Per-ad first/last campaign touch before signup (shared contract C2; written once by createUserOnFirebase,
  // first_touch may be filled later by the Worker from the mobile app's install attribution).
  acquisition_attribution?: AcquisitionAttribution | null;
  // The id the first-experience tests draw this account's sides from: the device id of the browser it signed
  // up in, so a visitor keeps the sides they saw before signing up (src/first-experience-tests/). Missing on
  // older accounts, which draw from their uid.
  experiment_assignment_unit_id?: string;
  // Sides of a first-experience test that must hold on every device, by experiment id (today only
  // free_allowance_2026_10, written when the account's country is first known).
  experiment_variants?: Record<string, string>;
  // Flat acquisition fields on user docs created by the Worker's partner signup route (no touches there).
  acquisition_utm_source?: string | null;
  acquisition_utm_medium?: string | null;
  acquisition_utm_campaign?: string | null;
  // TODO: do refactoring to add this field to user_settings
  save_card_default_option: "Anki" | "Jungle";
  user_settings?: {
    should_include_card_url_source_when_saving_to_anki: boolean;
    default_language_for_card_generation?: string;
    days_to_receive_a_review_reminder_on?: DaysOfTheWeek[];
    email_notification_preferences: UserEmailAndNotificationPreferences[];
    notification_preferences?: UserNotificationPreferences;
    status_review_reminders_notifications?: ReviewRemindersStatus;
    review_reminders_notifications_resumes_at?: any;
    question_types_user_selected_to_generate?: CardTypeWithDifficulty[];
    // The template the user's LAST generation actually ran with — new decks
    // default to it (falls back to Bloom when ineligible / out of quota).
    question_generation_template_id_last_used?: QuestionGenerationTemplateId;
    include_table_mcqs?: boolean;
    start_studying_deck_review_algorithm?: StartStudyingDeckReviewAlgorithmType;
    sound_effects_enabled?: boolean;
    should_rephrase_questions?: boolean;
    did_user_grant_push_notification_permissions?: boolean; // Properties for mobile app tracking
    desktop_push_notification_token?: string; // This is the token for the desktop push notifications
    // How many times the app has asked for notification permission (the desktop opt-in screen, or
    // `review_reminder` sent to the mobile app) and when it last did (notification-permission-asks.ts).
    notification_permission_prompt_count?: number;
    // When the tutor name poll was shown to this account, by poll id (src/features/tutor-name-poll/): it is
    // shown once per account, so a set entry keeps it from showing on any other device.
    tutor_name_poll_shown_at?: Record<string, unknown>;
    date_last_notification_permission_prompt?: any;
    // The language the app is shown in (one of the 14 Tolgee locales), which push notifications are written in.
    // Kept current by AppLanguageSync.
    app_language?: string;
    app_language_preference?: string;
    app_language_preference_revision?: string | null;
    app_language_updated_at?: string;
    app_language_source?: "web" | "ios" | "android";
    level_for_amount_of_cards_to_generate?: LevelForAmountOfCardsToGenerate; // the level of the deck for the amount of cards to generate, this is used to determine how many cards to generate for the deck
    has_widget_been_configured?: boolean; // this is true if the user has completed the setup for the iOS / Android widget
  };
  unreleased_features_user_has_given_feedback_to_list?: Array<UnreleasedFeatureToGetFeedbackOn>;
  features_user_has_seen_in_whats_new_list?: Array<NewFeaturesForWhatsNewModal>;
  email_communication_settings?: UserEmailAndNotificationPreferences;
  // this is the list of key features that the user has tried
  key_features_user_has_tried?: UserKeyFeatureTried[];

  // properties related to gamification features
  gamification_properties?: UserGamificationProperties;

  tracking_data?: {
    google_analytics_client_id?: string;
    google_analytics_session_id?: string;
  };

  // currently this info is captured only for Tec de Monterrey students
  age?: number;
  gender?: "Male" | "Female" | "Non-binary" | "Other";
  student_program?: TecDeMonterreyProgramType;
  semester?: number;
  comunidad_life?: ComunidadLifeType;
  student_id?: string; // in spanish this is "Matricula". This will be something like "A01286480"

  number_of_questions_answered?: number; // the number of questions the user has answered
  number_of_sets_of_cards_generated?: number; // the number of sets of cards the user has generated
  number_of_chat_messages_sent?: number; // the number of chat messages the user has sent
  number_of_review_session_started?: number; // the number of review sessions the user has started
  number_of_review_session_completed?: number; // the number of review sessions the user has completed
  number_of_times_homepage_visited?: number; // the number of times the user has visited the homepage
  // Counts presses of the post-answer explain button (both "explain my mistake"
  // and "explain why it's right"), starting from when this field was introduced
  // (absent = 0 for existing users). On the 3rd press we auto-launch a voice
  // call to drive voice-tutor discovery. See EXPLAIN_PRESSES_TO_AUTO_LAUNCH_VOICE.
  number_of_explain_button_presses?: number;
  // True once the user has started any voice call. Used to EXCLUDE users who
  // already discovered voice from the 3rd-explain-press auto-launch nudge.
  has_started_voice_call?: boolean;
  // Count of voice conversations the user has completed (written by the backend
  // on call-end). Read-only here. Combined with has_started_voice_call it lets
  // the nudge exclude users who discovered voice BEFORE has_started_voice_call
  // existed — no backend backfill needed.
  number_of_voice_conversations?: number;
  // "Prove mastery for 20% off" voice-challenge state. attempts_used gates the
  // MASTERY_CHALLENGE_MAX_ATTEMPTS lifetime attempts; discount is the earned
  // 24h grant the checkout reads.
  voice_mastery_challenge?: {
    attempts_used?: number;
    discount?: { granted_at?: number; expires_at?: number; consumed?: boolean };
  };
  has_user_visited_deck_overview_page?: boolean;
  has_user_rated_app?: boolean;
  country_code?: string;
  ip_address?: string;
  university?: {
    name: string;
    id?: string;
    countryCode?: string;
  };
  has_user_seen_tooltip_for_chat_with_document?: boolean;
  has_downloaded_desktop_app?: boolean;
  user_referrer_id?: string;

  // properties related to the classes that the user is a part of
  class_properties?: {
    class_decks_id?: string[]; // this is the list of deck ids that are classes the user is a part of
    deck_ids_from_classmates_that_have_been_added_to_users_review_schedule?: string[]; // this is the list of deck ids that are active for review reminders
  };

  exams_properties?: {
    exam_ids?: string[]; // this is the list of exam ids that belong to the user
  };

  //for migration of page_generation_data to decks
  is_page_generation_data_migrated_to_decks?: boolean;
  is_page_generation_data_migration_in_progress?: boolean;

  // for the jungle mobile app
  // This is the list of push notification tokens for the mobile devices the user has.
  mobile_devices?: {
    ios_tokens?: string[];
    android_tokens?: string[]; // This is the list of push notification tokens for the mobile devices the user has.
  };

  // Roles for user
  role?: "admin" | "user" | null;

  // Clinical exam stations (OSCE practice). Written by the backend's
  // routes/voice_clinical_exam_routes.py; the browser only reads them.
  // The learner's last result per examination, keyed by the catalog's exam id.
  voice_clinical_exam_station_progress?: Record<string, ClinicalExamStationProgressOnUser>;
  // A non-Elite learner's one free trial station. `completedAt` is set once
  // that station's marking has been saved, which ends the trial.
  voice_clinical_exam_station_free_trial?: {
    stationsStarted?: number;
    stationsGraded?: number;
    completedAt?: number;
  };

  // Flags related to the first card review experience
  did_user_complete_mini_review_on_first_review?: boolean; // Indicates if the first challenge has been completed
  did_user_see_first_free_response_question?: boolean; // Indicates if the first free response question was seen

  // flag related to whether the user has seen the info message about rephrased questions
  has_user_seen_rephrased_question_message?: boolean;

  // flag to track if user has seen subscription upsell during mini review completion
  has_user_seen_subscription_upsell_after_mini_review?: boolean;

  // tracking events for adjust (mobile app)
  tracking_events?: {
    did_track_first_generation_on_adjust?: boolean;
    did_track_one_generation_and_10_questions_answered_on_adjust?: boolean;
    did_track_one_generation_and_10_questions_answered_and_purchase_on_adjust?: boolean;
    did_track_one_generation_and_25_questions_answered_on_adjust?: boolean;
    did_track_three_generations_on_adjust?: boolean;
    did_track_three_generations_and_10_questions_answered_on_adjust?: boolean;
  };

  // DEPRECATED
  animal_avatar_selected?: string;
  animal_team_selected?: string;
  all_time_xp_earned?: number; // this value is replicated in the leaderboard collection
  last_xp_milestone_hit?: number; // current milestones we are tracking are 25, 50, 100, 200, 500, 1000
};

export type BadgeProgressStatus = "locked" | "unlocked" | "in_progress";
export type Badge = {
  badge_id: string;
  badge_progress_status: BadgeProgressStatus;
  date_earned: Date;
};

export type UserGamificationProperties = {
  character_team_selected: AnimalCharacter;
  character_with_accessory_selected: null | AnimalCharacterWithAccessory;
  current_level: number;
  total_xp_earned: number;
  xp_earned_at_current_level: number;
  xp_needed_to_reach_next_level: number;
  xp_earned_since_river_progress_was_last_seen: number;
  number_of_buddies_unlocked: number; // buddies are lil characters that you earn when you unlock all the trees
  last_xp_milestone_hit?: number; // // current milestones we are tracking are 25, 50, 100, 200, 500, 1000
  current_tree_being_used: Tree;
  unlocked_trees: Tree[]; // set of trees that the user has unlocked
  old_xp_earned?: number; // only for users that had xp earned before the new game design system was introduced in may 2025
  all_time_xp_earned?: number;
  current_week_xp_earned?: number;
  // for leaderboard leagues
  leaderboard_leagues_metadata: {
    current_league_rank_name: LeaderboardLeagues | null;
    league_ranks_unlocked: LeaderboardLeagues[];
    group_leagues_participated_in: [];
    number_of_times_promoted: 0;
    number_of_times_demoted: 0;
    number_of_times_with_no_change_in_league: 0;
    current_group_id: null;
    last_group_id_user_participated_in: null;
    last_position_in_leaderboard: number | null; // the last position the user was in the leaderboard
    should_show_end_of_week_league_results: false;
    last_position_viewed?: number | null; // the last position the user was viewed in the leaderboard
  };

  // For badges
  was_badges_the_last_visited_screen_in_backpack?: boolean; // Indicates whether the badges screen was the last visited section in the backpack; used to decide if the user should be redirected to badges instead of the main backpack view.
  badges_progress_and_stats?: {
    questions_answered_badges: {
      number_of_questions_answered: number;
      badges: Badge[];
    };
    free_response_questions_answered_badges: {
      number_of_free_response_questions_answered: number;
      badges: Badge[];
    };
    exams_survived_badges: {
      number_of_exams_survived: number;
      badges: Badge[];
    };
    daily_reviews_badges: {
      number_of_daily_reviews_completed: number;
      badges: Badge[];
    };
    mistakes_corrected_badges: {
      number_of_mistakes_corrected: number;
      badges: Badge[];
    };
    ids_of_earned_badges_user_has_not_seen: string[];
  };
};

export type CheckS3DocumentAvailabilityResult = {
  available: boolean;
  errorMessage?: string;
  downloadUrl?: string;
  estimatedHours?: number;
  estimatedCompletion?: string;
  status: string;
};

// ****** RECORDS ****** //
// ****** RECORDS ****** //
export const DeckColors: Record<string, DeckColor> = {
  color1: {
    background: "#A4EFA2",
    boxShadow: "0px 0px 16px 3px #7DFF79",
    borderColor: "#7EB87C",
  },
  color2: {
    background: "#E7A7E4",
    boxShadow: "0px 0px 16px 3px #F23FEA",
    borderColor: "#B885B5",
  },
  color3: {
    background: "#A2EFEF",
    boxShadow: "0px 0px 16px 3px #3FEFEF",
    borderColor: "#7DB8B8",
  },
  color4: {
    background: "#E9EFA2",
    boxShadow: "0px 0px 16px 3px #E7F537",
    borderColor: "#B3B87D",
  },
  color5: {
    background: "#EFB9A2",
    boxShadow: "0px 0px 16px 5px #f46e35",
    borderColor: "#B88E7D",
  },
};

export const examplePageGeneratedIds = ["VyJzKUJXAgIMPAgGodDw", "kUvB7Mq2IOcWD7L6VAY7", "NBRtMSWWptrPACfMEte7"];

// Default value for the card generation language.
export const USE_DOCUMENT_LANGUAGE_FOR_CARD_LANGUAGE_SETTING = "document_language";
export const DEFAULT_COUNTRY_SETTING = "us"; // default country setting for the user
export const CONSTRAINED_GENERATIONS_COUNTRY_CODES = ["BR", "PH", "IN", "BE", "FR"]; // Brazil, Philippines, India, Belgium, France

export type Concept = {
  conceptName: string;
  dateAdded: Date;
  hasGeneratedCaseQuestionsFromConcept: boolean;
  hasGeneratedKeyConceptCardsFromConcept: boolean;
  hasGeneratedSummaryOutlineFromConcept: boolean;
  isGeneratingCaseQuestionsFrom_concept: boolean;
  isGeneratingKeyConceptCardsFromConcept: boolean;
  isGeneratingSummaryOutlineFromConcept: boolean;
};

export type TopicMasteryProgressScore = {
  // users earn points for a topic based on how they answer questions related to that topic. for free response, wrong = 1 point, okay  = 2, correct = 3. for mcq, wrong = 1, correct = 2
  currentScore: number;
  maxPossibleScore: number;
};

export type KeyConceptMasteryProgressScoresAndRelatedTopicsProgressScores = {
  // users earn points for a topic based on how they answer questions related to that topic. for free response, wrong = 1 point, okay  = 2, correct = 3. for mcq, wrong = 1, correct = 2
  topicNamesAndMasteryProgressScores?: Record<string, TopicMasteryProgressScore>;
  maxConceptScore: number;
  currConceptScore: number;
};

export type KeyConceptNameToConceptMasteryProgressScoresAndRelatedTopicsProgressScores = Record<
  string,
  KeyConceptMasteryProgressScoresAndRelatedTopicsProgressScores
>;

/**
 * Events sent FROM the web app TO the mobile app
 * These are commands or requests that the web app sends to trigger actions on the mobile app
 */
export const enum EventSentToMobileApp {
  GUEST_INTERFACE_LANGUAGE = "guest_interface_language",
  SIGN_IN_WITH_GOOGLE = "sign_in_with_google",
  SIGN_IN_WITH_OUTLOOK = "sign_in_with_outlook",
  SIGN_IN_WITH_APPLE = "sign_in_with_apple",
  LOGOUT = "logout",
  INIT_APP = "init_app",
  PAYMENT_REQUEST = "payment_request",
  OPEN_PLATFORM_SUBSCRIPTION_MANAGEMENT = "open_platform_subscription_management",
  PAYMENT_REQUEST_WITH_SPECIFIC_SUBSCRIPTION = "payment_request_with_specific_subscription",
  RESTORE_SUBSCRIPTION = "restore_subscription",
  OPEN_URL = "open_url",
  OPEN_APP_SETTINGS = "open_app_settings",
  REVIEW_REMINDER = "review_reminder",
  PLAY_CORRECT_ANSWER_SOUND = "play_correct_answer_sound",
  DOWNLOAD_FILE = "download_file",
  RATE_APP = "rate_app",
  PLAY_SHORT_INCORRECT_ANSWER_SOUND = "play_incorrect_answer_sound_short",
  PLAY_LONG_INCORRECT_ANSWER_SOUND = "play_incorrect_answer_sound_long",
  PLAY_PRUNING_SOUND = "play_pruning_sound",
  PLAY_CALLING_SOUND_LOOP = "play_calling_sound_loop",
  STOP_CALLING_SOUND_LOOP = "stop_calling_sound_loop",
  PLAY_CALL_ANSWERED_SOUND = "play_call_answered_sound",
  PLAY_CALL_HANG_UP_SOUND = "play_call_hang_up_sound",
  /**
   * A Call Monkey voice call is starting (before the microphone is opened) /
   * has ended. The iOS shell switches its audio session into voice-chat mode
   * for the call so the WebView's microphone gets echo cancellation, then
   * restores its default. Values mirror the jungle-mobile enum.
   */
  VOICE_CALL_STARTED = "voice_call_started",
  VOICE_CALL_ENDED = "voice_call_ended",
  STATUS_BAR_COLOR = "status_bar_color",
  /**
   * The page turned light or dark (src/theme/mobileAppColorThemeBridge.ts). Sent only to an app version
   * that announced `colorThemeBridgeVersion`; it paints the bars around the page in the page's color
   * and picks light or dark status bar icons for it.
   */
  COLOR_THEME = "color_theme",
  /** Request a native iOS/Android WebView screenshot for customer support. */
  REQUEST_SUPPORT_SCREEN_CAPTURE = "request_support_screen_capture",
  REQUEST_SUPPORT_SCREEN_RECORDING_START = "request_support_screen_recording_start",
  REQUEST_SUPPORT_SCREEN_RECORDING_STOP = "request_support_screen_recording_stop",
  REQUEST_SUPPORT_SCREEN_RECORDING_CANCEL = "request_support_screen_recording_cancel",
  REQUEST_SUPPORT_SCREEN_RECORDING_CHUNK = "request_support_screen_recording_chunk",
  RELEASE_SUPPORT_SCREEN_RECORDING = "release_support_screen_recording",
  WIDGET_CONFIGURATION_INTENT = "widget_configuration_intent",
  RELOAD_WIDGET_DATA = "reload_widget_data",
  LOADED_PAGE = "loaded_page",
  USER_DATA = "user_data",
  DOWNLOAD_IMAGE = "download_image",
  SHARE = "share",

  /**
   * REQUEST_SUBSCRIPTION_PRICES_FROM_MOBILE_APP: Web app requests the mobile app to send subscription prices
   * Flow: Web sends this event → Mobile receives it → Mobile responds with SEND_SUBSCRIPTION_PRICES_TO_WEB
   * This is needed because mobile app stores have their own pricing that may differ from Stripe
   */
  REQUEST_SUBSCRIPTION_PRICES_FROM_MOBILE_APP = "request_subscription_prices_from_mobile_app",

  /**
   * WEB_CONTENT_ERROR: a root render failure caught by the web error boundary
   * (src/components/RootErrorBoundary.tsx). Native forwards it to its Sentry
   * as kind `render_error`. The native shell's injected listener posts the
   * same event for uncaught errors. String must match jungle-mobile
   * `JungleAppAndWebEvent.WEB_CONTENT_ERROR`.
   */
  WEB_CONTENT_ERROR = "web_content_error",

  /**
   * INSTALL_ATTRIBUTION_ID_TOKEN: the answer to REQUEST_INSTALL_ATTRIBUTION_ID_TOKEN. Message JSON
   * `{requestId, userId, idToken}`; `idToken` is the web Firebase user's ID token only when that user is
   * `userId`, else null (src/backend/utils/install-attribution-id-token.ts). String must match jungle-mobile's
   * `JungleAppAndWebEvent.INSTALL_ATTRIBUTION_ID_TOKEN`.
   */
  INSTALL_ATTRIBUTION_ID_TOKEN = "install_attribution_id_token",

  /**
   * PUSH_NOTIFICATION_OPEN_RECEIVED: the page got a PUSH_NOTIFICATION_OPENED message. Sent at once for every copy
   * of that message the page gets, with the message's `deliveryId` as the message, so the app does not send the
   * tap again (src/backend/utils/push-notification-opened.ts). String must match jungle-mobile's
   * `JungleAppAndWebEvent.PUSH_NOTIFICATION_OPEN_RECEIVED`.
   */
  PUSH_NOTIFICATION_OPEN_RECEIVED = "push_notification_open_received",
}

/**
 * Events received BY the web app FROM the mobile app
 * These are responses or notifications that the mobile app sends back to the web app
 */
export const enum EventReceivedFromMobileApp {
  INTERFACE_LANGUAGE_CONTEXT = "interface_language_context",
  INIT_APP = "init_app",
  APP_BUILD_INFO = "app_build_info",
  AUTHENTICATION_TOKEN = "authentication_token",
  AUTHENTICATION_SIGNED_OUT = "authentication_signed_out",
  ERROR_AUTHENTICATION = "error_authentication",
  PAYMENT_SUCCESS = "payment_success",
  PAYMENT_ERROR = "payment_error",
  PAYMENT_CANCELED = "payment_canceled",

  /**
   * SUBSCRIPTION_UPDATED: Mobile app sends refreshed user data after native IAP sync
   * so the WebView can close upsell modals without waiting on Firestore.
   */
  SUBSCRIPTION_UPDATED = "subscription_updated",

  /**
   * SEND_SUBSCRIPTION_PRICES_TO_WEB: Mobile app sends subscription prices to the web app
   * Flow: Web sends REQUEST_SUBSCRIPTION_PRICES_FROM_MOBILE_APP → Mobile receives it → Mobile responds with this event
   * Contains Apple App Store or Google Play Store pricing information
   */
  SEND_SUBSCRIPTION_PRICES_TO_WEB = "send_subscription_prices_to_web",
  WIDGET_CONFIGURATION_RESULT = "widget_configuration_result",

  IOS_VERSION_PUBLISHED = "ios_version_published",
  ANDROID_VERSION_PUBLISHED = "android_version_published",
  BACK_ACTION = "back_action",
  /** Native iOS/Android result for a matching support screenshot request. */
  SUPPORT_SCREEN_CAPTURE_RESULT = "support_screen_capture_result",
  SUPPORT_SCREEN_RECORDING_START_RESULT = "support_screen_recording_start_result",
  SUPPORT_SCREEN_RECORDING_STOP_RESULT = "support_screen_recording_stop_result",
  SUPPORT_SCREEN_RECORDING_CHUNK_RESULT = "support_screen_recording_chunk_result",
  /**
   * INSTALL_ATTRIBUTION: the Android app's install attribution when it arrives after the page loaded (it is
   * also injected as window.__JUNGLE_NATIVE_INSTALL_ATTRIBUTION__ before content loads). The message is the
   * JSON of {source: "play_install_referrer", params: {utm_*, fb_adset_id, fb_ad_id, fb_placement, fb_site}}:
   * the Google Play install referrer's ad parameters (src/utils/acquisition-attribution.ts). String must match
   * jungle-mobile's `JungleAppAndWebEvent.INSTALL_ATTRIBUTION`.
   */
  INSTALL_ATTRIBUTION = "install_attribution",
  /**
   * REQUEST_INSTALL_ATTRIBUTION_ID_TOKEN: the app asks for the web's Firebase ID token so an email (WebView-only)
   * session can post its install attribution to the Worker. Message JSON `{requestId, userId}`; the web answers
   * with EventSentToMobileApp.INSTALL_ATTRIBUTION_ID_TOKEN. String must match jungle-mobile's
   * `JungleAppAndWebEvent.REQUEST_INSTALL_ATTRIBUTION_ID_TOKEN`.
   */
  REQUEST_INSTALL_ATTRIBUTION_ID_TOKEN = "request_install_attribution_id_token",
  /**
   * PUSH_NOTIFICATION_OPENED: the learner tapped a push notification. Sent after the app received INIT_APP from
   * this page load. Message JSON `{notificationId, notificationType, redirectTo, appState, platformOS,
   * deliveryId}` (any value but `deliveryId` may be null). The web confirms every copy at once with
   * EventSentToMobileApp.PUSH_NOTIFICATION_OPEN_RECEIVED (the app sends a tap no page confirmed again after the
   * next INIT_APP, at most 3 times in all), and handles each `deliveryId` once: it tracks "Push Notification
   * Opened" (never with the `deliveryId`) and opens `/<redirectTo>` (src/utils/analytics/push-notification-opens.ts).
   * String must match jungle-mobile's `JungleAppAndWebEvent.PUSH_NOTIFICATION_OPENED`.
   */
  PUSH_NOTIFICATION_OPENED = "push_notification_opened",
}

export const enum WidgetConfigurationResult {
  OPENED = "opened",
  FAILED = "failed",
}

export type WidgetConfigurationResultMessage = {
  requestId: string;
  result: WidgetConfigurationResult;
};

export type SendOrReceiveMobileAppEvent = {
  typeEvent: EventSentToMobileApp | EventReceivedFromMobileApp;
  message: string;
};

export type LeaderboardLeagueGroupConfig = {
  leagueTier: number;
  leagueIcon: string;
  translationKey: string;
  defaultTranslation: string;
  numberOfTopUsersWhoArePromotedToTheNextLeague: number; //Number of highest ranked users who are promoted to the next league
  numberOfBottomUsersWhoAreDemotedToThePreviousLeague: number; //Number of lowest ranked users who are demoted to the previous league
  numberOfMiddleUsersWhoRemainInTheSameLeague: number; //Number of users who remain in the same league
  league_name: LeaderboardLeagues;
};

export const enum StartStudyingDeckReviewAlgorithmType {
  RESUME_WHERE_I_LEFT_OFF = "resume_where_i_left_off",
  UNANSWERED_FIRST = "unanswered_first",
  STUDY_MISTAKES_ONLY = "study_mistakes_only",
  PRIORITY_SPACED_REPETITION_ORDER = "priority_spaced_repetition_order",
  SHUFFLE_ORDER = "shuffle_order",
}

export type PriceDetails = {
  symbol?: String;
  monthlyMegaMindPrice?: String;
  annualMegaMindPrice?: String;
  monthlySuperLearnerPrice?: String;
  annualSuperLearnerPrice?: String;
  // NEW — Elite (PR 1). Optional so legacy worker responses that don't yet
  // include Elite prices don't fail validation.
  monthlyElitePrice?: String;
  annualElitePrice?: String;
  isMobileAppPrice?: boolean;
  priceOf7DayTrial?: String;
  currency?: String;
  // The exam pass's one-time prices in this currency, whole units (Stripe only; null where the Worker has none)
  examPassPrices?: { short: string | null; two_weeks: string | null; month: string | null };
};

export type AppleSubscriptionProductDetails = {
  productId: string;
  localizedPrice: string;
  price: string;
  currency: string;
  discounts: { price: string }[];
  period_subscription: string;
  subscriptionPeriodUnitIOS: string;
};

export type GooglePlaySubscriptionProductDetails = {
  subscriptionOfferDetails: {
    pricingPhases: {
      pricingPhaseList: {
        recurrenceMode: 1 | 2;
        priceAmountMicros: string;
        billingCycleCount: number;
        billingPeriod: string;
        priceCurrencyCode: string;
        formattedPrice: string;
      }[];
    };
    offerToken: string;
    offerTags: string[];
    offerId: string | null;
    basePlanId: string;
  }[];
  name: string;
  productType: "subs";
  description: string;
  title: string;
  productId: string;
  platform: "android";
};

export type UserInLeagueGroup = {
  user_id: string;
  last_question_answered_timestamp: Date; // used to handle cases when users are tied in XP. whoever answered the last question first will rank higher
  total_xp_earned: number;
  total_questions_answered: number; // may not be needed if we do sorting at time of loading leaderboard
  zone_position: LeaderboardPositionZone;
  position_in_group: number; // position of the user in the group
  user_name: string;
  character_with_accessory_selected: AnimalCharacterWithAccessory;
};
export type LeaderboardLeague = {
  league_name: LeaderboardLeagues;
  current_group_week_id: number;
  current_group_id?: string; //null when its a new week and the 1st group hasn't been created
  current_group_num_of_participants: number; //"number (0-30)", or null
};

export type LeaderboardLeagueGroup = {
  id: string; // (unique group identifier)",
  week_id: string; // Week identifier in the format "YYYYWww" where weeks start on Sunday 00:00 PT and end on Saturday 23:59 PT.
  league_name: LeaderboardLeagues;
  league_start_date: Date; // Date in PT of Sunday 00:00
  num_of_participants: number;
  created_at: Date;
  users_in_group: [UserInLeagueGroup];
  total_xp_earned_by_all_participants?: number; // updated by script and analytics
  total_questions_answered_by_all_participants?: number; // updated by script and analytics
  league_end_date: Date; // Date in PT of Saturday 23:59
};

// Helper function to get the appropriate monthly generation limit based on user's geography
export const getMonthlyGenerationLimit = (countryCode?: string): number => {
  if (!countryCode) {
    return NUM_OF_FREE_MONTHLY_GENERATIONS_ALLOWED;
  }

  countryCode = countryCode.toUpperCase();
  if (CONSTRAINED_GENERATIONS_COUNTRY_CODES.includes(countryCode)) {
    return NUM_OF_FREE_MONTHLY_GENERATIONS_ALLOWED_FOR_CONSTRAINED_GEOGRAPHIES;
  }

  return NUM_OF_FREE_MONTHLY_GENERATIONS_ALLOWED;
};

export const getInitialMonthlyGenerationsLeft = (
  countryCode?: string,
  haveCardsBeenGeneratedAnonymouslyBeforeSignup = false
): number => {
  const monthlyGenerationLimit = getMonthlyGenerationLimit(countryCode);
  const anonymousGenerationsUsed = haveCardsBeenGeneratedAnonymouslyBeforeSignup ? 1 : 0;

  return Math.max(0, monthlyGenerationLimit - anonymousGenerationsUsed);
};

export enum NotificationType {
  FeedbacksForAnswersReplenished = "feedbacks for answers replenished",
  ChatMessagesReplenished = "chat messages replenished",
  ReviewReminder = "review reminder",
  DesktopReviewReminder = "desktop review reminder",
}

// Test mode flag
export const STREAK_TEST_MODE_ENABLED = false; // Set to true when you want to use test collections
